import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import { Link, Prompt, useHistory, useLocation } from 'react-router-dom';
import Select, { components } from 'react-select';
import { KTSVG } from '../../../_metronic/helpers';

interface ShipmentFormProps {
    onSubmit?: (formData: ShipmentFormData) => void;
}

interface ShipmentFormData {
    name: string;
    originating_hub: string;
    destination_hub: string;
    date_of_journey: string;
    notes: string;
    booking_id_list: string[];
}

interface Hub {
    id: string;
    name: string;
}

interface BookingID {
    who_paid: any;
    invoice_value: number;
    nrml_payment_quantity: number;
    cbm_value: any;
    total_pieces: number;
    notes: string;
    id: string;
    booking_id: string;
    split_id: string;
    total_quantity: number;
    booking_mode: string;
    consignor: string;
    consignee: string;
    payment_mode: string;
    payment_status: string;
    large_box_quantity?: number;
    medium_box_quantity?: number;
    small_box_quantity?: number;
    piece_value?: number;
    cbm_price?: number;
    nrml_quantity_price?: number;
    invoice_percentage?: number;
}

interface BookingDetails {
    large_box_quantity: string;
    medium_box_quantity: string;
    small_box_quantity: string;
    id: string;
    booking_id: string;
    split_id: string;
    total_quantity: number;
    booking_mode: string;
    consignor: string;
    consignee: string;
    payment_mode: string;
    payment_status: string;
    large_journey_quantity?: number;
    medium_journey_quantity?: number;
    small_journey_quantity?: number;
    shipment_status: string;
    piece_value?: number;
    cbm_price?: number;
    nrml_quantity_price?: number;
    invoice_percentage?: number;
}

interface SplitData {
    invoice_value: number;
    nrml_payment_quantity: number;
    cbm_value: number;
    total_pieces: number;
    bookingId: string;
    totalQuantity: number;
    large_box_quantity?: number;
    medium_box_quantity?: number;
    small_box_quantity?: number;
    piece_value?: number;
    cbm_price?: number;
    nrml_quantity_price?: number;
    invoice_percentage?: number;
    notes: string;
    newBookingId?: string;
    originalBookingId?: string;
    isNewSplit?: boolean;
}

interface BookingOption {
    value: string;
    label: string;
    isDisabled?: boolean;
    booking: BookingID;
}

interface BulkUpdateRequest {
    booking_ids: string[];
    checked_status: "Yes" | "No";
}

interface BulkUpdateResponse {
    status: "success" | "error";
    message: string;
    failed_ids?: string[];
}

const ShipmentForm: React.FC<ShipmentFormProps> = ({ onSubmit }) => {
    // Existing states
    const [hubData, setHubData] = useState<Hub[]>([]);
    const [bookingId, setBookingId] = useState<BookingID[]>([]);
    const [showAddbookingpopup, setShowaddbookingPopup] = useState(false);
    const [selectedBookingIds, setSelectedBookingIds] = useState<string[]>([]);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const [splitData, setSplitData] = useState<Record<string, SplitData>>({});
    const [originalBookings, setOriginalBookings] = useState<Record<string, string>>({});
    const [isSplitedresponseDone, setisSplitedresponseDone] = useState(false);
    const [issplitopen, setissplitopen] = useState(false);
    const [iscancelOpen, setisCancelopen] = useState(false);
    const [afterAddjourney, setAfterAddjourney] = useState(false);
    const [Bokkingdetails, setBokkingdetails] = useState<BookingDetails[]>([]);
    const [loading, setLoading] = useState(false)
    const [splitBookingIds, setSplitBookingIds] = useState<Set<string>>(new Set());
    const [allSelectedBookingIds, setAllSelectedBookingIds] = useState<string[]>([]);
    const [openSplitForms, setOpenSplitForms] = useState<Set<string>>(new Set());
    const [selectedOptions, setSelectedOptions] = useState<BookingOption[]>([]);
    const [addedBookingIds, setAddedBookingIds] = useState<string[]>([]);
    const [notesData, setNotesData] = useState<Record<string, string>>({});
    const [loadingNotes, setLoadingNotes] = useState<Record<string, boolean>>({});
    const [openNotesForms, setOpenNotesForms] = useState<Set<string>>(new Set());
    const [cancelingBookingIds, setCancelingBookingIds] = useState<Set<string>>(new Set());
    const [loadingBookingIds, setLoadingBookingIds] = useState<Set<string>>(new Set());
    const [isUnloadingBlocked, setIsUnloadingBlocked] = useState(false);
    const [isBulkSelecting, setIsBulkSelecting] = useState(false);
    const [showSelectAllConfirm, setShowSelectAllConfirm] = useState(false);
    
    // New states for optimized bulk operations
    const [batchSize] = useState(50); // Process 50 IDs per batch
    const [isBatchProcessing, setIsBatchProcessing] = useState(false);
    const [batchProgress, setBatchProgress] = useState({ current: 0, total: 0 });
    const [batchOperationType, setBatchOperationType] = useState<'select' | 'remove' | null>(null);
    const [cancellationToken, setCancellationToken] = useState<{ isCancelled: boolean }>({ isCancelled: false });
    const [successfulBatchIds, setSuccessfulBatchIds] = useState<string[]>([]);
    const [failedBatchIds, setFailedBatchIds] = useState<string[]>([]);
    const [showBatchCancelConfirm, setShowBatchCancelConfirm] = useState(false);

    const [formData, setFormData] = useState<ShipmentFormData>({
        name: '',
        originating_hub: '',
        destination_hub: '',
        date_of_journey: '',
        notes: '',
        booking_id_list: []
    });

    const history = useHistory();
    const SELECT_ALL_OPTION: BookingOption = {
        value: "Select All",
        label: "Select All",
        booking: {} as BookingID,
    };

    const selectedBookingIdsRef = useRef(selectedBookingIds);
    const addedBookingIdsRef = useRef(addedBookingIds);

    useEffect(() => {
        addedBookingIdsRef.current = addedBookingIds;
    }, [addedBookingIds]);

    useEffect(() => {
        selectedBookingIdsRef.current = selectedBookingIds;
    }, [selectedBookingIds]);

    // Function to send API request
    const sendCancelRequest = () => {
        const bookingIdsToSend = [
            ...addedBookingIdsRef.current,
            ...selectedBookingIdsRef.current,
        ];
        const uniqueBookingIds = Array.from(new Set(bookingIdsToSend));

        if (uniqueBookingIds.length > 0) {
            fetch(
                'https://backendcway.causewaycargo.com/superadmin/afteraddjourneycancel',
                {
                    method: 'POST',
                    headers: {
                        auth: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6Nywicm9sZSI6InN1cGVyX2FkbWluIiwiaWF0IjoxNzI2ODM0MzIwfQ.edXyvu0znbVoi5KU3iL8FPww3qe8LMU0oXOCwdZi8do',
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ booking_id_list: uniqueBookingIds }),
                    keepalive: true,
                }
            ).catch((error) => {
                console.error('Error sending cancel request:', error);
            });
        }
    };

    useEffect(() => {
        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            const bookingIdsToSend = [
                ...addedBookingIdsRef.current,
                ...selectedBookingIdsRef.current,
            ];

            if (bookingIdsToSend.length > 0 || isUnloadingBlocked) {
                event.preventDefault();
                event.returnValue = 'Are you sure you want to leave? Changes may not be saved.';

                if (isUnloadingBlocked) {
                    sendCancelRequest();
                }
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [isUnloadingBlocked]);

    const location = useLocation();
    const shouldBlockNavigation =
        location.pathname === '/shipment/shipmentform' &&
        (selectedBookingIdsRef.current.length > 0 ||
            addedBookingIdsRef.current.length > 0);

    // Optimized batch API call function
    const makeBatchApiCall = useCallback(async (url: string, method: string, body: any) => {
        try {
            const response = await fetch(url, {
                method,
                headers: {
                    'auth': 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6Nywicm9sZSI6InN1cGVyX2FkbWluIiwiaWF0IjoxNzI2ODM0MzIwfQ.edXyvu0znbVoi5KU3iL8FPww3qe8LMU0oXOCwdZi8do',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(body)
            });
            return await response.json();
        } catch (error) {
            console.error("Batch API Error:", error);
            throw error;
        }
    }, []);

    const convertBookingsToOptions = useCallback((): BookingOption[] => {
        const bookingOptions = bookingId.map(booking => {
            const id = booking.split_id || booking.booking_id;
            const isDisabled = splitBookingIds.has(id);

            return {
                value: id,
                label: id,
                isDisabled,
                booking
            };
        });

        return [SELECT_ALL_OPTION, ...bookingOptions];
    }, [bookingId, splitBookingIds]);

    useEffect(() => {
        const options = selectedBookingIds.map(id => {
            const booking = getBookingDetails(id);
            return booking ? {
                value: id,
                label: id,
                booking: booking,
                isDisabled: splitBookingIds.has(id)
            } : null;
        }).filter(Boolean) as BookingOption[];

        setSelectedOptions(options);
    }, [selectedBookingIds, splitBookingIds]);

    useEffect(() => {
        fetchHubdropdown();
    }, []);

    const updatedBookingIds = useMemo(() => {
        return Array.from(new Set([...addedBookingIds, ...selectedBookingIds]));
    }, [addedBookingIds, selectedBookingIds]);

    useEffect(() => {
        setAllSelectedBookingIds(updatedBookingIds);
    }, [updatedBookingIds]);

    useEffect(() => {
        setFormData(prevFormData => ({
            ...prevFormData,
            booking_id_list: updatedBookingIds
        }));
    }, [updatedBookingIds]);

    const handleSubmit = async (e: React.FormEvent) => {
        setLoading(true)
        e.preventDefault();
        try {
            const response = await makeApiCall(
                "https://backendcway.causewaycargo.com/superadmin/addshipment",
                "POST",
                formData
            );

            if (response?.status === "success") {
                toast.success(response.message || "Shipment added successfully!");
                setAfterAddjourney(false)
                setSelectedBookingIds([])
                setFormData({
                    name: '',
                    originating_hub: '',
                    destination_hub: '',
                    date_of_journey: '',
                    notes: '',
                    booking_id_list: []
                });

                selectedBookingIdsRef.current = [];
                addedBookingIdsRef.current = [];

                setTimeout(() => {
                    history.push('/shipment');
                }, 2500);
                console.log("Response:", response);
                onSubmit?.(formData);
                setLoading(false)
            }
        } catch (error: any) {
            toast.error(error.message || "Error adding shipment");
            console.error("Error:", error);
        }
    };

    const handleInputChange = (field: keyof ShipmentFormData, value: string) => {
        setFormData(prev => {
            if (
                (field === "originating_hub" && value === prev.destination_hub) ||
                (field === "destination_hub" && value === prev.originating_hub)
            ) {
                toast.error("Originating hub and Destination hub cannot be the same.");
                return prev;
            }

            return {
                ...prev,
                [field]: value
            };
        });
    };

    const handleBookingSelection = async (bookingIdentifier: string) => {
        console.log("triggered handleBookingSelection")

        setSelectedBookingIds((prev) => {
            const isCurrentlySelected = prev.includes(bookingIdentifier);
            const updatedIds = isCurrentlySelected
                ? prev.filter(id => id !== bookingIdentifier)
                : [...prev, bookingIdentifier];

            const checkedStatus = isCurrentlySelected ? "No" : "Yes";
            console.log("checkedStatus", checkedStatus)

            makeApiCall("https://backendcway.causewaycargo.com/superadmin/updatebookingstatus", "PUT", {
                booking_id: bookingIdentifier,
                checked_status: checkedStatus
            })
                .then(response => {
                    if (response?.status !== "success") {
                        setSelectedBookingIds(prev);
                        toast.error(`Failed to update status for ${bookingIdentifier}`);
                    }
                })
                .catch(error => {
                    setSelectedBookingIds(prev);
                    console.error(`Error updating booking ${bookingIdentifier} status:`, error);
                    toast.error(`Failed to update status for ${bookingIdentifier}`);
                });

            return updatedIds;
        });
    };

    const removeBooking = async (id: string) => {
        if (splitBookingIds.has(id)) {
            toast.error(`Booking ${id} has been split and cannot be removed.`);
            return;
        }

        setSelectedBookingIds(prev => prev.filter(selectedId => selectedId !== id));

        try {
            const response = await makeApiCall("https://backendcway.causewaycargo.com/superadmin/updatebookingstatus", "PUT", {
                booking_id: id,
                checked_status: "No"
            });

            if (response?.status === "success") {
                setSplitData(prev => {
                    const newData = { ...prev };
                    delete newData[id];
                    return newData;
                });
            } else {
                console.error("Failed to update booking status");
                toast.error(`Failed to remove booking ${id}`);
            }
        } catch (error) {
            console.error("Error updating booking status:", error);
            toast.error(`Error removing booking ${id}`);
        }
    };

    const getBookingDetails = (id: string) => {
        return bookingId.find(booking =>
            (booking.booking_id === id || booking.split_id === id) &&
            (!booking.split_id || booking.split_id === id)
        );
    };

    const fetchHubdropdown = async () => {
        try {
            const response = await fetch("https://backendcway.causewaycargo.com/superadmin/getallhublist", {
                method: 'GET',
                headers: {
                    'auth': 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6OCwicm9sZSI6InN1cGVyX2FkbWluIiwiaWF0IjoxNzQxMTUwMjI0fQ.BoQy0I28tpGeO2eL70pTX0iqn8mXpnJ27GExnpOka6o',
                    'Content-Type': 'application/json',
                },
            });
            const result = await response.json();
            setHubData(result?.data);
        } catch (error) {
            console.error("Error fetching hub list:", error);
        }
    };

    const fetchAllbookingidforship = async () => {
        try {
            const response = await fetch("https://backendcway.causewaycargo.com/superadmin/allbookingforship", {
                method: 'GET',
                headers: {
                    'auth': 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6OCwicm9sZSI6InN1cGVyX2FkbWluIiwiaWF0IjoxNzQxMTUwMjI0fQ.BoQy0I28tpGeO2eL70pTX0iqn8mXpnJ27GExnpOka6o',
                    'Content-Type': 'application/json',
                },
            });
            const result = await response.json();

            const normalizedBookings = result?.data.map((booking: BookingID) => ({
                ...booking,
                uniqueId: booking.split_id || booking.booking_id
            }));

            setBookingId(normalizedBookings);
        } catch (error) {
            console.error("Error fetching booking list:", error);
            toast.error("Failed to fetch booking list");
        }
    };

    const afterAddJourneshowdetails = async () => {
        if (selectedBookingIds.length === 0) {
            console.warn("No bookings selected for the journey.");
            return;
        }

        try {
            const bookingIdsToSend = [...addedBookingIds, ...selectedBookingIds];
            const uniqueBookingIds = Array.from(new Set(bookingIdsToSend));

            const response = await fetch("https://backendcway.causewaycargo.com/superadmin/addjourneybookinglist", {
                method: 'POST',
                headers: {
                    'auth': 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6OCwicm9sZSI6InN1cGVyX2FkbWluIiwiaWF0IjoxNzQxMTUwMjI0fQ.BoQy0I28tpGeO2eL70pTX0iqn8mXpnJ27GExnpOka6o',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    booking_id_list: uniqueBookingIds
                })
            });

            const result = await response.json();

            if (response.ok) {
                setAddedBookingIds(prev => {
                    const newAddedIds = [...prev, ...selectedBookingIds];
                    return Array.from(new Set(newAddedIds));
                });
                setBokkingdetails(result?.data)
                setSelectedBookingIds([]);
                setOpenSplitForms(new Set());
                setSplitData({});
                return true;
            } else {
                console.error("Failed to add journey:", result);
                return false;
            }
        } catch (error) {
            console.error("Error adding journey:", error);
            return false;
        }
    };

    const afterAddJournyCancel = async () => {
        if (updatedBookingIds.length === 0) {
            console.warn("No bookings selected for cancellation.");
            return;
        }

        try {
            const bookingIdsToSend = [...addedBookingIds, ...selectedBookingIds];
            const uniqueBookingIds = Array.from(new Set(bookingIdsToSend));

            const response = await fetch("https://backendcway.causewaycargo.com/superadmin/afteraddjourneycancel", {
                method: 'POST',
                headers: {
                    'auth': 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6OCwicm9sZSI6InN1cGVyX2FkbWluIiwiaWF0IjoxNzQxMTUwMjI0fQ.BoQy0I28tpGeO2eL70pTX0iqn8mXpnJ27GExnpOka6o',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    booking_id_list: uniqueBookingIds
                })
            });

            const result = await response.json();

            if (response.ok) {
                setAddedBookingIds([]);
                setSelectedBookingIds([]);
                setAfterAddjourney(false);
                return true;
            } else {
                console.error("Failed to cancel journey:", result);
                return false;
            }
        } catch (error) {
            console.error("Error cancelling journey:", error);
            return false;
        }
    };

    const makeApiCall = async (url: string, method: string, body: any) => {
        try {
            const response = await fetch(url, {
                method,
                headers: {
                    'auth': 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6Nywicm9sZSI6InN1cGVyX2FkbWluIiwiaWF0IjoxNzI2ODM0MzIwfQ.edXyvu0znbVoi5KU3iL8FPww3qe8LMU0oXOCwdZi8do',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(body)
            });
            return await response.json();
        } catch (error) {
            console.error("API Error:", error);
            throw error;
        }
    };

    const handleNormalSplit = async (bookingData: BookingID, data: SplitData) => {
        try {
            if (!bookingData.booking_id && !bookingData.split_id) {
                throw new Error("Invalid booking: No booking_id or split_id found");
            }

            if (!data.totalQuantity ||
                data.totalQuantity <= 0 ||
                data.totalQuantity >= bookingData.total_quantity) {
                alert(`Total quantity must be between 1 and ${bookingData.total_quantity - 1}`);
                return;
            }

            const isAlreadySplit = (bookingData.split_id && bookingData.split_id.includes('-')) ||
                (bookingData.booking_id && bookingData.booking_id.includes('-'));

            const currentBookingId = bookingData.booking_id || bookingData.split_id;

            const response = await makeApiCall(
                "https://backendcway.causewaycargo.com/superadmin/normalsplitbooking",
                "POST",
                {
                    booking_id: currentBookingId,
                    journey_quantity: Number(data.totalQuantity),
                    common_method_value: Number(
                        data.total_pieces ||
                        data.cbm_value ||
                        0
                    ),
                    split_notes: data.notes
                }
            );

            if (response?.status === "success") {
                const updateData = isAlreadySplit ? response.data : response.insert_data;

                if (!updateData) {
                    throw new Error("Invalid response: Missing required data");
                }

                const newSplitId = updateData.split_id || (isAlreadySplit ? currentBookingId : null);

                if (!newSplitId) {
                    throw new Error("Invalid split ID received from server");
                }

                setSelectedBookingIds(prev => {
                    const updatedIds = prev.filter(id => id !== currentBookingId);
                    return [...updatedIds, newSplitId];
                });

                const updatedBooking = {
                    ...bookingData,
                    booking_id: "",
                    split_id: newSplitId,
                    total_quantity: Number(updateData.total_quantity),
                    notes: updateData.notes,
                    total_pieces: updateData.total_pieces,
                    cbm_value: updateData.cbm_value
                };

                setSelectedOptions(prev => {
                    const updatedOptions = prev.filter(opt => opt.value !== currentBookingId);
                    return [...updatedOptions, {
                        value: newSplitId,
                        label: newSplitId,
                        booking: updatedBooking,
                        isDisabled: false
                    }];
                });

                setBookingId(prev => {
                    return prev.map(booking => {
                        if (booking.booking_id === currentBookingId || booking.split_id === currentBookingId) {
                            return updatedBooking;
                        }
                        return booking;
                    });
                });

                setSplitBookingIds(prev => {
                    const newSet = new Set(prev);
                    newSet.add(newSplitId);
                    return newSet;
                });

                if (!isAlreadySplit) {
                    setOriginalBookings(prev => ({
                        ...prev,
                        [newSplitId]: currentBookingId
                    }));
                }

                setAllSelectedBookingIds(prev => {
                    const updatedIds = prev.filter(id => id !== currentBookingId);
                    return [...updatedIds, newSplitId];
                });

                setSplitData(prev => {
                    const newData = { ...prev };
                    delete newData[currentBookingId];
                    return newData;
                });

                setOpenSplitForms(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(currentBookingId);
                    return newSet;
                });

                setissplitopen(false);
                setisCancelopen(true);

                alert("Booking split successfully!");
            } else {
                throw new Error("Split booking failed: " + response?.message);
            }
        } catch (error) {
            console.error("Normal split error:", error);
            alert("Failed to split booking: " + (error instanceof Error ? error.message : "Unknown error"));
        }
    };

    const handleBoxSplit = async (bookingData: BookingID, data: SplitData) => {
        try {
            if (!bookingData.booking_id && !bookingData.split_id) {
                throw new Error("Invalid booking: No booking_id or split_id found");
            }

            console.log("bookingData", bookingData);

            const largeBoxQty = Number(data.large_box_quantity || 0);
            const mediumBoxQty = Number(data.medium_box_quantity || 0);
            const smallBoxQty = Number(data.small_box_quantity || 0);
            const totalPieces = Number(data.total_pieces || 0);
            const originalTotalPieces = Number(bookingData.total_pieces || 0);

            if (largeBoxQty === 0 && mediumBoxQty === 0 && smallBoxQty === 0) {
                alert("Error: At least one box quantity must be greater than zero.");
                return;
            }

            if (
                largeBoxQty === Number(bookingData.large_box_quantity || 0) &&
                mediumBoxQty === Number(bookingData.medium_box_quantity || 0) &&
                smallBoxQty === Number(bookingData.small_box_quantity || 0)
            ) {
                alert("Error: At least one box quantity must change.");
                return;
            }

            const isAlreadySplit = (bookingData.split_id && bookingData.split_id.includes('-')) ||
                (bookingData.booking_id && bookingData.booking_id.includes('-'));

            const currentBookingId = bookingData.booking_id || bookingData.split_id;

            const response = await makeApiCall(
                "https://backendcway.causewaycargo.com/superadmin/addsplitbooking",
                "POST",
                {
                    booking_id: currentBookingId,
                    large_quantity: largeBoxQty,
                    medium_quantity: mediumBoxQty,
                    small_quantity: smallBoxQty,
                    common_method_value: Number(data.total_pieces || data.cbm_value || 0),
                    split_notes: data.notes
                }
            );

            if (response?.status === "success") {
                const updateData = isAlreadySplit ? response.data : response.insert_data;

                if (!updateData) {
                    throw new Error("Invalid response: Missing required data");
                }

                const newSplitId = updateData.split_id || (isAlreadySplit ? currentBookingId : null);

                if (!newSplitId) {
                    throw new Error("Invalid split ID received from server");
                }

                const updatedBooking = {
                    ...bookingData,
                    booking_id: "",
                    split_id: newSplitId,
                    large_box_quantity: Number(updateData.large_box_quantity || 0),
                    medium_box_quantity: Number(updateData.medium_box_quantity || 0),
                    small_box_quantity: Number(updateData.small_box_quantity || 0),
                    total_quantity: Number(updateData.total_quantity),
                    total_pieces: updateData.total_pieces,
                    cbm_value: updateData.cbm_value,
                    notes: updateData.notes
                };

                setSelectedBookingIds(prev => {
                    const updatedIds = prev.filter(id => id !== currentBookingId);
                    return [...updatedIds, newSplitId];
                });

                setSelectedOptions(prev => {
                    const updatedOptions = prev.filter(opt => opt.value !== currentBookingId);
                    return [...updatedOptions, {
                        value: newSplitId,
                        label: newSplitId,
                        booking: updatedBooking,
                        isDisabled: false
                    }];
                });

                setBookingId(prev => {
                    return prev.map(booking => {
                        if (booking.booking_id === currentBookingId || booking.split_id === currentBookingId) {
                            return updatedBooking;
                        }
                        return booking;
                    });
                });

                setSplitBookingIds(prev => {
                    const newSet = new Set(prev);
                    newSet.add(newSplitId);
                    return newSet;
                });

                if (!isAlreadySplit) {
                    setOriginalBookings(prev => ({
                        ...prev,
                        [newSplitId]: currentBookingId
                    }));
                }

                setAllSelectedBookingIds(prev => {
                    const updatedIds = prev.filter(id => id !== currentBookingId);
                    return [...updatedIds, newSplitId];
                });

                setSplitData(prev => {
                    const newData = { ...prev };
                    delete newData[currentBookingId];
                    return newData;
                });

                setOpenSplitForms(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(currentBookingId);
                    return newSet;
                });

                setissplitopen(false);
                setisCancelopen(true);

                alert("Booking split successfully!");
            } else {
                throw new Error("Split booking failed: " + response?.message);
            }
        } catch (error) {
            console.error("Box split error:", error);
            alert("Failed to split booking: " + (error instanceof Error ? error.message : "Unknown error"));
        }
    };

    const handleSplitSubmit = async (bookingId: string) => {
        const data = splitData[bookingId];
        const booking = getBookingDetails(bookingId);
        if (!data || !booking) return;
        const whoPaid = booking.who_paid;

        setLoadingBookingIds(prev => {
            const newSet = new Set(prev);
            newSet.add(bookingId);
            return newSet;
        });

        try {
            if (booking.booking_mode === 'normal booking') {
                const totalQty = data.totalQuantity;
                const defaultTotalQty = booking.total_quantity;

                const paymentMethod = booking.payment_mode;

                const totalPieces = Number(data.total_pieces || 0);
                const originalTotalPieces = Number(booking.total_pieces || 0);

                const cbmValue = Number(data.cbm_value || 0);
                const originalCBMValue = Number(booking.cbm_value || 0);

                if (!totalQty || totalQty <= 0 || totalQty > defaultTotalQty || totalQty === defaultTotalQty) {
                    alert("Please enter a valid total quantity");
                    return;
                }
                if (whoPaid === "Consignor" && (booking.payment_mode === "Piece Rate Method" || booking.payment_mode === "CBM Rate Method")) {
                    let isValidChange = false;

                    if (paymentMethod === "Piece Rate Method") {
                        if (totalPieces < originalTotalPieces) {
                            isValidChange = true;
                        } else {
                            alert("Error: Total pieces must be reduced and not remain the same.");
                            return;
                        }
                    } else if (paymentMethod === "CBM Rate Method") {
                        if (cbmValue < originalCBMValue) {
                            isValidChange = true;
                        } else {
                            alert("Error: CBM value must be reduced and not remain the same.");
                            return;
                        }
                    }

                    if (!isValidChange) {
                        alert("Error: Either total pieces (Piece Rate) or CBM value (CBM Rate) must be reduced.");
                        return;
                    }

                    if (
                        totalQty > defaultTotalQty
                    ) {
                        alert("Error: Box quantities cannot exceed original values.");
                        return;
                    }
                }

                await handleNormalSplit(booking, data);
            } else {
                const largeQty = Number(data.large_box_quantity || 0);
                const mediumQty = Number(data.medium_box_quantity || 0);
                const smallQty = Number(data.small_box_quantity || 0);

                const originalLargeQty = Number(booking.large_box_quantity || 0);
                const originalMediumQty = Number(booking.medium_box_quantity || 0);
                const originalSmallQty = Number(booking.small_box_quantity || 0);

                const totalPieces = Number(data.total_pieces || 0);
                const originalTotalPieces = Number(booking.total_pieces || 0);

                const cbmValue = Number(data.cbm_value || 0);
                const originalCBMValue = Number(booking.cbm_value || 0);

                const paymentMethod = booking.payment_mode;

                const isBoxQuantitySame = (
                    largeQty === originalLargeQty &&
                    mediumQty === originalMediumQty &&
                    smallQty === originalSmallQty
                );

                if (isBoxQuantitySame) {
                    alert("Error: At least one box quantity must be reduced.");
                    return;
                }

                if (whoPaid === "Consignor" && (booking.payment_mode === "Piece Rate Method" || booking.payment_mode === "CBM Rate Method")) {
                    let isValidChange = false;

                    if (paymentMethod === "Piece Rate Method") {
                        if (totalPieces < originalTotalPieces) {
                            isValidChange = true;
                        } else {
                            alert("Error: Total pieces must be reduced and not remain the same.");
                            return;
                        }
                    } else if (paymentMethod === "CBM Rate Method") {
                        if (cbmValue < originalCBMValue) {
                            isValidChange = true;
                        } else {
                            alert("Error: CBM value must be reduced and not remain the same.");
                            return;
                        }
                    }

                    if (!isValidChange) {
                        alert("Error: Either total pieces (Piece Rate) or CBM value (CBM Rate) must be reduced.");
                        return;
                    }

                    if (
                        largeQty > originalLargeQty ||
                        mediumQty > originalMediumQty ||
                        smallQty > originalSmallQty
                    ) {
                        alert("Error: Box quantities cannot exceed original values.");
                        return;
                    }
                }

                await handleBoxSplit(booking, data);
            }
            setOpenSplitForms(prev => {
                const newSet = new Set(prev);
                newSet.delete(bookingId);
                return newSet;
            });
        } catch (error) {
            console.error("Split submission error:", error);
        } finally {
            setLoadingBookingIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(bookingId);
                return newSet;
            });
        }
    };

    const handleSplitCancel = async (booking: BookingID) => {
        if (!window.confirm("Are you sure you want to cancel this split?")) {
            return;
        }

        const currentId = booking.split_id || booking.booking_id;
        console.log("currentId", currentId)
        if (!currentId) return;

        setCancelingBookingIds(prev => {
            const newSet = new Set(prev);
            newSet.add(currentId);
            return newSet;
        });

        let originalId = originalBookings[currentId];

        if (!originalId && currentId.includes('-')) {
            originalId = currentId
        }

        try {
            const response = await makeApiCall(
                "https://backendcway.causewaycargo.com/superadmin/cancelsplit",
                "POST",
                {
                    old_booking_id: originalId,
                    new_booking_id: currentId,
                    booking_id_list: [originalId, currentId]
                }
            );

            if (response?.status === "success") {
                setSelectedBookingIds(prev => {
                    const withoutCurrent = prev.filter(id => id !== currentId);
                    if (!withoutCurrent.includes(originalId)) {
                        withoutCurrent.push(originalId);
                    }
                    return Array.from(new Set(withoutCurrent));
                });

                const updatedOriginalBooking = {
                    ...booking,
                    booking_id: originalId,
                    split_id: "",
                    total_quantity: response.data?.total_quantity || booking.total_quantity,
                    large_box_quantity: response.data?.large_box_quantity,
                    medium_box_quantity: response.data?.medium_box_quantity,
                    small_box_quantity: response.data?.small_box_quantity,
                    total_pieces: response.data?.total_pieces,
                    cbm_value: response.data?.cbm_value,
                    notes: response.data?.notes
                };

                setSelectedOptions(prev => {
                    const filteredOptions = prev.filter(opt => opt.value !== currentId);
                    const originalOption = {
                        value: originalId,
                        label: originalId,
                        booking: updatedOriginalBooking,
                        isDisabled: false
                    };

                    return [...filteredOptions, originalOption];
                });
                setBookingId(prev => {
                    return prev.map(b => {
                        if (b.split_id === currentId || b.booking_id === originalId) {
                            return updatedOriginalBooking;
                        }
                        return b;
                    });
                });

                setSplitBookingIds(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(currentId);
                    return newSet;
                });

                setOpenSplitForms(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(currentId);
                    return newSet;
                });

                setOriginalBookings(prev => {
                    const newData = { ...prev };
                    delete newData[currentId];
                    return newData;
                });

                setSplitData(prev => {
                    const newData = { ...prev };
                    delete newData[currentId];
                    return newData;
                });

                setAllSelectedBookingIds(prev => {
                    const filtered = prev.filter(id => id !== currentId);
                    if (!filtered.includes(originalId)) {
                        filtered.push(originalId);
                    }
                    return filtered;
                });

                alert("Split cancelled successfully!");
            }
        } catch (error) {
            console.error("Cancel split error:", error);
            alert("Failed to cancel split");
        } finally {
            setCancelingBookingIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(currentId);
                return newSet;
            });
        }
    };

    const getPaymentFieldLabel = (paymentMode: string): string => {
        switch (paymentMode) {
            case 'Piece Rate Method':
                return 'Piece Value';
            case 'CBM Rate Method':
                return 'CBM Value';
            case 'Normal Unit Price Method':
            case 'Splitted Unit Price Method':
                return 'Unit Price Value';
            case 'Invoice Value Method':
                return 'Invoice Value';
            default:
                return '';
        }
    };

    const handleSplitInputChange = (bookingId: string, field: keyof SplitData, value: string) => {
        console.log(`Updating ${field} for booking ${bookingId} to:`, value);

        setSplitData(prev => {
            const updatedBooking = {
                ...prev[bookingId],
                [field]: field === 'notes' ? value : Number(value),
                bookingId
            };

            console.log('Updated booking data:', updatedBooking);

            return {
                ...prev,
                [bookingId]: updatedBooking
            };
        });
    };

    const handleSplitClick = (booking: BookingID) => {
        const currentId = booking.split_id || booking.booking_id;
        setOpenSplitForms(prev => {
            const newSet = new Set(prev);
            if (newSet.has(currentId)) {
                newSet.delete(currentId);
            } else {
                newSet.add(currentId);
                setOpenNotesForms(prevNotes => {
                    const notesSet = new Set(prevNotes);
                    notesSet.delete(currentId);
                    return notesSet;
                });
            }
            return newSet;
        });

        if (!openSplitForms.has(currentId)) {
            const isAlreadySplit = (booking.split_id && booking.split_id.includes('-'));
            const originalId = isAlreadySplit ?
                (booking.split_id?.split('-')[0] || booking.booking_id) :
                booking.booking_id;

            setSplitData(prev => ({
                ...prev,
                [currentId]: {
                    ...prev[currentId],
                    bookingId: currentId,
                    totalQuantity: booking.total_quantity,
                    large_box_quantity: booking.large_box_quantity || 0,
                    medium_box_quantity: booking.medium_box_quantity || 0,
                    small_box_quantity: booking.small_box_quantity || 0,
                    notes: booking.notes,
                    total_pieces: booking.total_pieces,
                    cbm_value: booking.cbm_value,
                    nrml_payment_quantity: booking.nrml_payment_quantity,
                    invoice_value: booking.invoice_value,
                    originalBookingId: originalId,
                    isNewSplit: !isAlreadySplit
                }
            }));
        }
    };

    // OPTIMIZED BULK OPERATIONS FUNCTIONS

    const handleBulkSelectAll = async () => {
        setShowSelectAllConfirm(false);
        
        const allOptions = convertBookingsToOptions()
            .filter(opt => opt.value !== SELECT_ALL_OPTION.value && !opt.isDisabled);
        
        const idsToAdd = allOptions
            .map(opt => opt.value)
            .filter(id => !selectedBookingIds.includes(id));
        
        if (idsToAdd.length === 0) {
            return;
        }
        
        setCancellationToken({ isCancelled: false });
        setSuccessfulBatchIds([]);
        setFailedBatchIds([]);
        
        setBatchOperationType('select');
        setIsBatchProcessing(true);
        setBatchProgress({ current: 0, total: idsToAdd.length });
        setIsUnloadingBlocked(true);
        
        const batches: string[][] = [];
        for (let i = 0; i < idsToAdd.length; i += batchSize) {
            batches.push(idsToAdd.slice(i, i + batchSize));
        }
        
        const successfulIds: string[] = [];
        const failedIds: string[] = [];
        
        try {
            for (let i = 0; i < batches.length; i++) {
                if (cancellationToken.isCancelled) {
                    break;
                }
                
                const batch = batches[i];
                
                try {
                    const response = await makeBatchApiCall(
                        "https://backendcway.causewaycargo.com/superadmin/updatebookingstatus",
                        "PUT",
                        {
                            booking_ids: batch,
                            checked_status: "Yes"
                        }
                    );
                    
                    if (response?.status === "success") {
                        successfulIds.push(...batch);
                        setSuccessfulBatchIds(prev => [...prev, ...batch]);
                        
                        setSelectedBookingIds(prev => {
                            const newIds = [...prev];
                            batch.forEach(id => {
                                if (!newIds.includes(id)) {
                                    newIds.push(id);
                                }
                            });
                            return newIds;
                        });
                    } else {
                        console.warn("Batch API failed, falling back to individual calls");
                        for (const id of batch) {
                            try {
                                const individualResponse = await makeApiCall(
                                    "https://backendcway.causewaycargo.com/superadmin/updatebookingstatus",
                                    "PUT",
                                    {
                                        booking_id: id,
                                        checked_status: "Yes"
                                    }
                                );
                                
                                if (individualResponse?.status === "success") {
                                    successfulIds.push(id);
                                    setSuccessfulBatchIds(prev => [...prev, id]);
                                    
                                    setSelectedBookingIds(prev => {
                                        if (!prev.includes(id)) {
                                            return [...prev, id];
                                        }
                                        return prev;
                                    });
                                } else {
                                    failedIds.push(id);
                                    setFailedBatchIds(prev => [...prev, id]);
                                }
                            } catch (error) {
                                console.error(`Error selecting booking ${id}:`, error);
                                failedIds.push(id);
                                setFailedBatchIds(prev => [...prev, id]);
                            }
                        }
                    }
                    
                    setBatchProgress(prev => ({
                        current: Math.min(prev.current + batch.length, prev.total),
                        total: prev.total
                    }));
                    
                } catch (error) {
                    console.error(`Error processing batch ${i + 1}:`, error);
                    failedIds.push(...batch);
                    setFailedBatchIds(prev => [...prev, ...batch]);
                    setBatchProgress(prev => ({
                        current: Math.min(prev.current + batch.length, prev.total),
                        total: prev.total
                    }));
                }
            }
            
            if (cancellationToken.isCancelled) {
                toast.info(`Bulk selection was cancelled. ${successfulIds.length} bookings were selected.`);
                
                if (successfulIds.length > 0) {
                    try {
                        await makeBatchApiCall(
                            "https://backendcway.causewaycargo.com/superadmin/updatebookingstatus",
                            "PUT",
                            {
                                booking_ids: successfulIds,
                                checked_status: "No"
                            }
                        );
                        
                        setSelectedBookingIds(prev => prev.filter(id => !successfulIds.includes(id)));
                        setSuccessfulBatchIds([]);
                        
                    } catch (rollbackError) {
                        console.error("Error rolling back selections:", rollbackError);
                        toast.error("Some bookings may still be selected. Please refresh the page.");
                    }
                }
            } else if (failedIds.length > 0) {
                toast.warning(`Successfully selected ${successfulIds.length} bookings. Failed to select ${failedIds.length} bookings.`);
            } else {
                toast.success(`Successfully selected ${successfulIds.length} bookings.`);
            }
            
        } catch (error) {
            console.error("Bulk selection error:", error);
            toast.error("An error occurred during bulk selection");
        } finally {
            setIsUnloadingBlocked(false);
            setIsBatchProcessing(false);
            setBatchOperationType(null);
            setBatchProgress({ current: 0, total: 0 });
            setCancellationToken({ isCancelled: false });
        }
    };

    const handleBulkRemove = async () => {
        const idsToRemove = selectedBookingIds.filter(id => !splitBookingIds.has(id));
        
        if (idsToRemove.length === 0) {
            return;
        }
        
        setCancellationToken({ isCancelled: false });
        setSuccessfulBatchIds([]);
        setFailedBatchIds([]);
        
        setBatchOperationType('remove');
        setIsUnloadingBlocked(true);
        setIsBatchProcessing(true);
        setBatchProgress({ current: 0, total: idsToRemove.length });
        
        const batches: string[][] = [];
        for (let i = 0; i < idsToRemove.length; i += batchSize) {
            batches.push(idsToRemove.slice(i, i + batchSize));
        }
        
        const successfulIds: string[] = [];
        const failedIds: string[] = [];
        
        try {
            for (let i = 0; i < batches.length; i++) {
                if (cancellationToken.isCancelled) {
                    break;
                }
                
                const batch = batches[i];
                
                try {
                    const response = await makeBatchApiCall(
                        "https://backendcway.causewaycargo.com/superadmin/updatebookingstatus",
                        "PUT",
                        {
                            booking_ids: batch,
                            checked_status: "No"
                        }
                    );
                    
                    if (response?.status === "success") {
                        successfulIds.push(...batch);
                        setSuccessfulBatchIds(prev => [...prev, ...batch]);
                        
                        setSelectedBookingIds(prev => prev.filter(id => !batch.includes(id)));
                    } else {
                        console.warn("Batch API failed, falling back to individual calls");
                        for (const id of batch) {
                            try {
                                const individualResponse = await makeApiCall(
                                    "https://backendcway.causewaycargo.com/superadmin/updatebookingstatus",
                                    "PUT",
                                    {
                                        booking_id: id,
                                        checked_status: "No"
                                    }
                                );
                                
                                if (individualResponse?.status === "success") {
                                    successfulIds.push(id);
                                    setSuccessfulBatchIds(prev => [...prev, id]);
                                    
                                    setSelectedBookingIds(prev => prev.filter(selectedId => selectedId !== id));
                                } else {
                                    failedIds.push(id);
                                    setFailedBatchIds(prev => [...prev, id]);
                                }
                            } catch (error) {
                                console.error(`Error removing booking ${id}:`, error);
                                failedIds.push(id);
                                setFailedBatchIds(prev => [...prev, id]);
                            }
                        }
                    }
                    
                    setBatchProgress(prev => ({
                        current: Math.min(prev.current + batch.length, prev.total),
                        total: prev.total
                    }));
                    
                } catch (error) {
                    console.error(`Error processing batch ${i + 1}:`, error);
                    failedIds.push(...batch);
                    setFailedBatchIds(prev => [...prev, ...batch]);
                    setBatchProgress(prev => ({
                        current: Math.min(prev.current + batch.length, prev.total),
                        total: prev.total
                    }));
                }
            }
            
            if (cancellationToken.isCancelled) {
                toast.info(`Bulk removal was cancelled. ${successfulIds.length} bookings were removed.`);
                
                if (successfulIds.length > 0) {
                    try {
                        await makeBatchApiCall(
                            "https://backendcway.causewaycargo.com/superadmin/updatebookingstatus",
                            "PUT",
                            {
                                booking_ids: successfulIds,
                                checked_status: "Yes"
                            }
                        );
                        
                        setSelectedBookingIds(prev => {
                            const newIds = [...prev];
                            successfulIds.forEach(id => {
                                if (!newIds.includes(id)) {
                                    newIds.push(id);
                                }
                            });
                            return newIds;
                        });
                        setSuccessfulBatchIds([]);
                        
                    } catch (rollbackError) {
                        console.error("Error rolling back removals:", rollbackError);
                        toast.error("Some bookings may still be removed. Please refresh the page.");
                    }
                }
            } else if (failedIds.length > 0) {
                toast.warning(`Successfully removed ${successfulIds.length} bookings. Failed to remove ${failedIds.length} bookings.`);
            } else {
                toast.success(`Successfully removed ${successfulIds.length} bookings.`);
            }
            
        } catch (error) {
            console.error("Bulk removal error:", error);
            toast.error("An error occurred during bulk removal");
        } finally {
            setIsUnloadingBlocked(false);
            setIsBatchProcessing(false);
            setBatchOperationType(null);
            setBatchProgress({ current: 0, total: 0 });
            setCancellationToken({ isCancelled: false });
            
            const splitOptions = selectedOptions.filter(option => splitBookingIds.has(option.value));
            setSelectedOptions(splitOptions);
            setSelectedBookingIds(splitOptions.map(opt => opt.value));
            setAllSelectedBookingIds(splitOptions.map(opt => opt.value));
        }
    };

    const cancelBatchOperation = () => {
        setCancellationToken({ isCancelled: true });
        setShowBatchCancelConfirm(false);
    };

    const handleSelectChange = async (selected: BookingOption[] | null, actionMeta: any) => {
        if (actionMeta.action === 'remove-value') {
            const removedId = actionMeta.removedValue.value;
            if (!splitBookingIds.has(removedId)) {
                handleBookingSelection(removedId);
            }
        } else if (actionMeta.action === 'select-option') {
            const addedId = actionMeta.option.value;
            if (addedId === SELECT_ALL_OPTION.value) {
                setShowSelectAllConfirm(true);
                return;
            }
            handleBookingSelection(addedId);
        } else if (actionMeta.action === 'clear') {
            await handleBulkRemove();
            return;
        }
        
        let newSelectedOptions: BookingOption[] = [];
        
        if (selected) {
            newSelectedOptions = selected.filter(option => option.value !== SELECT_ALL_OPTION.value);
            
            splitBookingIds.forEach(splitId => {
                if (!newSelectedOptions.some(opt => opt.value === splitId)) {
                    const splitBooking = getBookingDetails(splitId);
                    if (splitBooking) {
                        newSelectedOptions.push({
                            value: splitId,
                            label: splitId,
                            booking: splitBooking,
                            isDisabled: true
                        });
                    }
                }
            });
        } else {
            Array.from(splitBookingIds).forEach(splitId => {
                const splitBooking = getBookingDetails(splitId);
                if (splitBooking) {
                    newSelectedOptions.push({
                        value: splitId,
                        label: splitId,
                        booking: splitBooking,
                        isDisabled: true
                    });
                }
            });
        }
        
        setSelectedOptions([...newSelectedOptions]);
        setSelectedBookingIds(newSelectedOptions.map(opt => opt.value));
        setAllSelectedBookingIds(newSelectedOptions.map(opt => opt.value));
    };

    const handleNotesIconClick = async (bookingKey: string) => {
        setOpenNotesForms(prev => {
            const newSet = new Set(prev);
            if (newSet.has(bookingKey)) {
                newSet.delete(bookingKey);
            } else {
                newSet.add(bookingKey);
                fetchNotesData(bookingKey);
                setOpenSplitForms(prevSplit => {
                    const splitSet = new Set(prevSplit);
                    splitSet.delete(bookingKey);
                    return splitSet;
                });
            }
            return newSet;
        });
    };

    const fetchNotesData = async (bookingKey: string) => {
        try {
            const response = await fetch('https://backendcway.causewaycargo.com/superadmin/bookingnotes', {
                method: 'POST',
                headers: {
                    "Content-Type": "application/json",
                    auth: "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6Nywicm9sZSI6InN1cGVyX2FkbWluIiwiaWF0IjoxNzI2ODM0MzIwfQ.edXyvu0znbVoi5KU3iL8FPww3qe8LMU0oXOCwdZi8do",
                },
                body: JSON.stringify({ common_id: bookingKey }),
            });

            if (!response.ok) {
                const errorResult = await response.json();
                const message = errorResult.message || "Failed to fetch notes.";
                throw new Error(message);
            }

            const result = await response.json();

            setNotesData(prev => ({
                ...prev,
                [bookingKey]: result?.data?.[0]?.notes || "",
            }));
        } catch (err) {
            console.error("Failed to fetch notes:", err);
            toast.error(err instanceof Error ? err.message : "An unexpected error occurred while fetching notes.");
        } finally {
            setLoadingNotes(prev => ({ ...prev, [bookingKey]: false }));
        }
    };

    const handleNotesSubmit = async (bookingKey: string) => {
        const note = notesData[bookingKey]?.trim();

        if (!note) {
            toast.error("Please enter a note before submitting.");
            return;
        }

        try {
            setLoadingNotes(prev => ({ ...prev, [bookingKey]: true }));

            const res = await fetch('https://backendcway.causewaycargo.com/superadmin/updatebookingnotes', {
                method: 'PUT',
                headers: {
                    "Content-Type": "application/json",
                    auth: "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6Nywicm9sZSI6InN1cGVyX2FkbWluIiwiaWF0IjoxNzI2ODM0MzIwfQ.edXyvu0znbVoi5KU3iL8FPww3qe8LMU0oXOCwdZi8do",
                },
                body: JSON.stringify({
                    common_id: bookingKey,
                    notes: note,
                }),
            });

            const result = await res.json();

            if (result.status === 'success') {
                toast.success("Notes updated successfully!");
                setNotesData(prev => ({ ...prev, [bookingKey]: '' }));
                setOpenNotesForms(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(bookingKey);
                    return newSet;
                });
            } else {
                toast.error(result.message || "Failed to update notes.");
            }
        } catch (error) {
            console.error("Error submitting notes:", error);
            toast.error("An error occurred while submitting the notes.");
        } finally {
            setLoadingNotes(prev => ({ ...prev, [bookingKey]: true }));
        }
    };

    useEffect(() => {
        if (isBatchProcessing) {
            document.body.style.overflow = 'hidden';
            document.body.style.pointerEvents = 'none';
        } else {
            document.body.style.overflow = 'auto';
            document.body.style.pointerEvents = 'auto';
        }

        return () => {
            document.body.style.overflow = 'auto';
            document.body.style.pointerEvents = 'auto';
        };
    }, [isBatchProcessing]);

    const formatOptionLabel = ({ value, booking }: BookingOption) => {
        if (value === "Select All") {
            return (
                <div className="d-flex align-items-center justify-content-between w-100 px-2 py-1 fw-bold">
                    {value}
                </div>
            );
        }

        return (
            <div className="d-flex align-items-center justify-content-between w-100 px-2 py-1">
                <span className="fw-bold">{value}</span>
                {booking?.booking_mode === "split booking" ? (
                    <span className="text-muted small d-flex gap-2">
                        <span>L: <strong>{booking.large_box_quantity || 0}</strong></span>
                        <span>M: <strong>{booking.medium_box_quantity || 0}</strong></span>
                        <span>S: <strong>{booking.small_box_quantity || 0}</strong></span>
                    </span>
                ) : (
                    <span className="text-muted small">
                        Qty: <strong>{booking?.total_quantity || 0}</strong>
                    </span>
                )}
            </div>
        );
    };

    const MultiValueContainer = (props: any) => {
        const { data, innerProps } = props;
        const id = data.value;
        const isSplitBooking = splitBookingIds.has(id);

        return (
            <div
                className={`badge d-flex align-items-center gap-2 p-3 m-1 ${isSplitBooking ? 'bg-primary' : 'bg-dark'}`}
                title={isSplitBooking ? "Split booking ID (cannot be removed)" : ""}
            >
                {id}
                {!isSplitBooking && (
                    <i
                        className="fas fa-times cursor-pointer ml-2"
                        onClick={(e) => {
                            e.stopPropagation();
                            removeBooking(id);
                        }}
                    ></i>
                )}
            </div>
        );
    };

    const modalStyles: React.CSSProperties = {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: showAddbookingpopup ? 'flex' : 'none',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1050
    };

    const modalContentStyles: React.CSSProperties = {
        width: '1200px',
        maxWidth: '100%'
    };

    const batchOperationModalStyles: React.CSSProperties = {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        display: isBatchProcessing ? 'flex' : 'none',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        cursor: 'not-allowed'
    };

    const batchOperationContentStyles: React.CSSProperties = {
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '30px',
        maxWidth: '500px',
        width: '90%',
        textAlign: 'center',
        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)'
    };

    const percentageCounterStyle = `
        @keyframes slideUp {
            0% { transform: translateY(100%); opacity: 0; }
            100% { transform: translateY(0); opacity: 1; }
        }
    `;

    return (
        <>
            <style>{percentageCounterStyle}</style>
            <Prompt
                when={shouldBlockNavigation}
                message={(nextLocation) => {
                    const confirmLeave = window.confirm(
                        'Are you sure you want to leave this page? Unsaved changes will be lost.'
                    );
                    if (confirmLeave) {
                        sendCancelRequest();
                    }
                    return confirmLeave;
                }}
            />
            <div className="card">
                <div className="card-body">
                    <form onSubmit={handleSubmit}>
                        <h3 className="card-title">Add Shipment</h3>

                        <div className="row mb-6">
                            <div className="col-md-4">
                                <div className="form-group">
                                    <label className="required form-label">Name</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={formData.name}
                                        onChange={(e) => handleInputChange('name', e.target.value)}
                                        required
                                    />
                                </div>
                            </div>

                            <div className="col-md-4">
                                <div className="form-group">
                                    <label className="required form-label">Originating Hub</label>
                                    <select
                                        className="form-select"
                                        value={formData.originating_hub}
                                        onChange={(e) => handleInputChange('originating_hub', e.target.value)}
                                        required
                                    >
                                        <option value="">Select Hub Address</option>
                                        {hubData &&
                                            hubData.map((hub, index) => (
                                                <option key={index} value={hub.name}>{hub.name}</option>
                                            ))}
                                    </select>
                                </div>
                            </div>

                            <div className="col-md-4">
                                <div className="form-group">
                                    <label className="required form-label">Destination Hub</label>
                                    <select
                                        className="form-select"
                                        value={formData.destination_hub}
                                        onChange={(e) => handleInputChange('destination_hub', e.target.value)}
                                        required
                                    >
                                        <option value="">Select Hub Address</option>
                                        {hubData &&
                                            hubData.map((hub, index) => (
                                                <option key={index} value={hub.name}>{hub.name}</option>
                                            ))}
                                    </select>
                                </div>
                            </div>
                        </div>

                        <div className="row mb-6">
                            <div className="col-md-4">
                                <div className="form-group">
                                    <label className="required form-label">Date of Journey</label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={formData.date_of_journey}
                                        min={new Date().toISOString().split("T")[0]}
                                        onFocus={(e) => (e.target as any).showPicker?.()}
                                        onClick={(e) => (e.target as any).showPicker?.()}
                                        onChange={(e) => handleInputChange("date_of_journey", e.target.value)}
                                        required
                                        placeholder='enter date'
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-12 d-flex justify-content-end">
                                <button
                                    type="button"
                                    className="btn btn-warning"
                                    onClick={() => {
                                        fetchAllbookingidforship();
                                        setShowaddbookingPopup(true);
                                    }}
                                >
                                    Add Bookings
                                </button>
                            </div>
                        </div>
                        {afterAddjourney && Bokkingdetails?.length > 0 && (
                            <div className="mt-4 ">
                                <h4>Booking Details</h4>
                                <div className="table-responsive border-top mb-5 p-2" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                    <table className="table table-row-bordered table-hover">
                                        <thead>
                                            <tr className="fw-bold fs-6 text-dark-800" >
                                                <th className='p-3'>S.No </th>
                                                <th className='p-3'>Booking ID</th>
                                                <th className='p-3'>Consignor</th>
                                                <th className='p-3'>Consignee</th>
                                                <th className='p-3'>Booking Mode</th>
                                                <th className='p-3'>Total Quantity</th>
                                                <th className='p-3'>Large Journey Quantity</th>
                                                <th className='p-3'>Medium Journey Quantity</th>
                                                <th className='p-3'>Small Journey Quantity</th>
                                                <th className='p-3'>Shipment Status</th>
                                            </tr>
                                        </thead>
                                        <tbody   >
                                            {Bokkingdetails.map((booking, index) => (
                                                <tr key={booking.id} className=''>
                                                    <td className='p-3'>{index + 1}</td>
                                                    <td className='p-3'>{booking.booking_id || booking.split_id}</td>
                                                    <td className='p-3'>{booking.consignor}</td>
                                                    <td className='p-3'>{booking.consignee}</td>
                                                    <td className='p-3'>{booking.booking_mode}</td>
                                                    <td className='p-3'>{booking.total_quantity}</td>
                                                    {booking.booking_mode === "split booking" ? (
                                                        <>
                                                            <td className='p-3'>{booking.large_box_quantity || "-"}</td>
                                                            <td className='p-3'>{booking.medium_box_quantity || "-"}</td>
                                                            <td className='p-3'>{booking.small_box_quantity || "-"}</td>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <td className='p-3'>0</td>
                                                            <td className='p-3'>0</td>
                                                            <td className='p-3'>0</td>
                                                        </>
                                                    )}
                                                    <td>{booking.shipment_status === "collected to shipment" ? "Shipment progress" : booking.shipment_status}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                        {afterAddjourney && Bokkingdetails?.length > 0 && (<div className="d-flex gap-2 justify-content-end">
                            <button
                                type="button"
                                className="btn btn-warning"
                                onClick={() => {
                                    const confirmCancel = window.confirm("Are you sure you want to cancel?");
                                    if (confirmCancel) {
                                        afterAddJournyCancel();
                                        setAllSelectedBookingIds([]);
                                        setSelectedBookingIds([]);
                                        setAfterAddjourney(false)
                                    }
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="btn btn-primary"
                                onClick={() => { }}
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        Submitting...
                                        <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                                    </>
                                ) : (
                                    "Submit"
                                )}
                            </button>
                        </div>)}
                    </form>
                </div>
                <ToastContainer />
            </div>

            {/* Booking Selection Modal */}
            <div style={modalStyles}>
                <div className="modal-dialog" style={modalContentStyles}>
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Select Bookings</h5>
                            <button
                                type="button"
                                className={`btn-close cursor-pointer ${selectedBookingIds.length > 0 ? "hover:cursor-not-allowed opacity-50" : ""}`}
                                onClick={() =>
                                    setShowaddbookingPopup(false)
                                }
                                disabled={selectedBookingIds.length > 0}
                            ></button>
                        </div>
                        <div className="modal-body">
                            <div className="form-group mb-4">
                                <div className="position-relative" ref={dropdownRef}>
                                    <Select
                                        isMulti
                                        id="booking-select"
                                        name="booking-select"
                                        value={selectedOptions}
                                        onChange={handleSelectChange}
                                        options={convertBookingsToOptions()}
                                        formatOptionLabel={formatOptionLabel}
                                        components={{
                                            MultiValueContainer,
                                            MultiValueRemove: (props: any) => {
                                                const { data } = props;
                                                if (splitBookingIds.has(data.value)) {
                                                    return null;
                                                }
                                                return (
                                                    <components.MultiValueRemove {...props} />
                                                );
                                            }
                                        }}
                                        closeMenuOnSelect={false}
                                        placeholder="Choose Booking ID"
                                        noOptionsMessage={() => "No bookings available"}
                                        isClearable={true}
                                        styles={{
                                            control: (provided: any) => ({
                                                ...provided,
                                                cursor: 'pointer',
                                                minWidth: '200px',
                                                width: '100%',
                                                outline: 'none',
                                                maxHeight: '150px',
                                                overflow: 'auto',
                                            }),
                                            multiValue: (provided: any, state: any) => ({
                                                ...provided,
                                                margin: 0,
                                                padding: 0,
                                                backgroundColor: splitBookingIds.has(state.data.value) ? '#e6f7ff' : provided.backgroundColor,
                                                borderColor: splitBookingIds.has(state.data.value) ? '#1890ff' : provided.borderColor,
                                            }),
                                            multiValueRemove: (provided: any, state: any) => ({
                                                ...provided,
                                                display: splitBookingIds.has(state.data.value) ? 'none' : 'flex',
                                            }),
                                        }}
                                        className="w-100"
                                        menuPlacement="auto"
                                    />
                                </div>
                            </div>

                            {!isDropdownOpen && selectedBookingIds.length > 0 && (
                                <div className="mt-4 border p-3 " style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                    <h6 className="mb-3 mt-3">Selected Bookings - {selectedBookingIds.length}</h6>
                                    <div className="table-responsive">
                                        <table className="table table-row-bordered table-hover align-middle">
                                            <thead>
                                                <tr className="fw-bold fs-6 text-gray-800">
                                                    <th className="text-start">Actions</th>
                                                    <th className="text-center">Booking ID</th>
                                                    <th className="text-center">Booking Mode</th>
                                                    <th className="text-center">Consignor</th>
                                                    <th className="text-center">Consignee</th>
                                                    <th className="text-center">Total Quantity</th>
                                                    <th className="text-center">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {selectedBookingIds.map((bookingId) => {
                                                    const booking = getBookingDetails(bookingId);
                                                    if (!booking) return null;

                                                    return (
                                                        <>
                                                            <tr key={bookingId}>
                                                                <td className="text-start">
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-sm btn-danger"
                                                                        onClick={() => removeBooking(booking.booking_id || booking.split_id)}
                                                                    >
                                                                        Remove
                                                                    </button>
                                                                </td>
                                                                <td className="text-center">{booking.booking_id ? booking.booking_id : booking.split_id || '-'}</td>
                                                                <td className="text-capitalize text-center">{booking.booking_mode}</td>
                                                                <td className="text-center" title={booking.consignor} style={{ cursor: "pointer" }}>
                                                                    {booking?.consignor?.length > 20 ? `${booking?.consignor?.slice(0, 20)}...` : booking.consignor}
                                                                </td>
                                                                <td className="text-center" title={booking.consignee} style={{ cursor: "pointer" }}>
                                                                    {booking?.consignee?.length > 20 ? `${booking?.consignee?.slice(0, 20)}...` : booking.consignee}
                                                                </td>
                                                                <td className="text-center">{booking.total_quantity}</td>
                                                                <td className='text-end'>
                                                                    <button
                                                                        className='btn btn-icon btn-light btn-hover-primary btn-sm me-1'
                                                                        onClick={() => handleSplitClick(booking)}
                                                                    >
                                                                        <KTSVG path='/media/icons/duotune/arrows/arr030.svg' className='svg-icon-3' />
                                                                    </button>
                                                                    <button
                                                                        className='btn btn-icon btn-light btn-hover-danger btn-sm'
                                                                        onClick={() => handleNotesIconClick(booking.booking_id || booking.split_id)}
                                                                    >
                                                                        <KTSVG path='/media/icons/duotune/communication/com007.svg' className='svg-icon-3' />
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                            {openSplitForms.has(booking.booking_id || booking.split_id) && (
                                                                <tr>
                                                                    <td colSpan={7}>
                                                                        <div className="d-flex flex-column gap-3 p-3 bg-light">
                                                                            {booking.booking_mode === 'normal booking' ? (
                                                                                <div className="d-flex gap-3 align-items-start flex-wrap">
                                                                                    <div className="form-group mb-0">
                                                                                        <label className="required form-label">Total Quantity</label>
                                                                                        <input
                                                                                            type="text"
                                                                                            className="form-control"
                                                                                            value={splitData[bookingId]?.totalQuantity || ''}
                                                                                            onChange={(e) => handleSplitInputChange(booking.booking_id || booking.split_id, 'totalQuantity', e.target.value)}
                                                                                            onKeyDown={(e) => {
                                                                                                const allowedKeys = [
                                                                                                    'Backspace',
                                                                                                    'Delete',
                                                                                                    'ArrowLeft',
                                                                                                    'ArrowRight',
                                                                                                    'Tab',
                                                                                                ];
                                                                                                if (
                                                                                                    !/^\d$/.test(e.key) && 
                                                                                                    !allowedKeys.includes(e.key)
                                                                                                ) {
                                                                                                    e.preventDefault();
                                                                                                }
                                                                                            }}
                                                                                            {...(splitBookingIds.has(booking.booking_id || booking.split_id)
                                                                                                ? { readOnly: true }
                                                                                                : { required: true })}
                                                                                            required
                                                                                        />
                                                                                    </div>
                                                                                    {booking.payment_mode && (booking.payment_mode === 'Piece Rate Method' || booking.payment_mode === 'CBM Rate Method') && (
                                                                                        <div className="form-group mb-0">
                                                                                            <label className="required form-label">
                                                                                                {getPaymentFieldLabel(booking.payment_mode)}
                                                                                            </label>
                                                                                            <input
                                                                                                type="number"
                                                                                                className="form-control"
                                                                                                value={
                                                                                                    booking.payment_mode === 'Piece Rate Method'
                                                                                                        ? splitData[bookingId]?.total_pieces ?? 0
                                                                                                        : booking.payment_mode === 'CBM Rate Method'
                                                                                                            ? splitData[bookingId]?.cbm_value ?? 0 : ""
                                                                                                }
                                                                                                onChange={(e) =>
                                                                                                    handleSplitInputChange(
                                                                                                        booking.booking_id || booking.split_id,
                                                                                                        (booking.payment_mode === 'Piece Rate Method'
                                                                                                            ? 'total_pieces'
                                                                                                            : booking.payment_mode === 'CBM Rate Method'
                                                                                                                ? 'cbm_value'
                                                                                                                : 'total_pieces') as keyof SplitData,
                                                                                                        e.target.value
                                                                                                    )
                                                                                                }
                                                                                                {...(splitBookingIds.has(booking.booking_id || booking.split_id)
                                                                                                    ? { readOnly: true }
                                                                                                    : { required: true })}
                                                                                            />
                                                                                        </div>
                                                                                    )}
                                                                                </div>
                                                                            ) : (
                                                                                <div className="d-flex gap-3 align-items-start flex-wrap">
                                                                                    <div className="form-group mb-0">
                                                                                        <label className="required form-label">Large Box Quantity</label>
                                                                                        <input
                                                                                            type="text"
                                                                                            className="form-control"
                                                                                            value={splitData[bookingId]?.large_box_quantity ?? 0}
                                                                                            onChange={(e) => handleSplitInputChange(booking.booking_id || booking.split_id, 'large_box_quantity', e.target.value)}
                                                                                            onKeyDown={(e) => {
                                                                                                const allowedKeys = [
                                                                                                    'Backspace',
                                                                                                    'Delete',
                                                                                                    'ArrowLeft',
                                                                                                    'ArrowRight',
                                                                                                    'Tab',
                                                                                                ];
                                                                                                if (
                                                                                                    !/^\d$/.test(e.key) && 
                                                                                                    !allowedKeys.includes(e.key)
                                                                                                ) {
                                                                                                    e.preventDefault();
                                                                                                }
                                                                                            }}
                                                                                            {...(splitBookingIds.has(booking.booking_id || booking.split_id)
                                                                                                ? { readOnly: true }
                                                                                                : { required: true })}
                                                                                        />
                                                                                    </div>
                                                                                    <div className="form-group mb-0">
                                                                                        <label className="required form-label">Medium Box Quantity</label>
                                                                                        <input
                                                                                            type="text"
                                                                                            className="form-control"
                                                                                            value={splitData[bookingId]?.medium_box_quantity ?? 0}
                                                                                            onChange={(e) => handleSplitInputChange(booking.booking_id || booking.split_id, 'medium_box_quantity', e.target.value)}
                                                                                            onKeyDown={(e) => {
                                                                                                const allowedKeys = [
                                                                                                    'Backspace',
                                                                                                    'Delete',
                                                                                                    'ArrowLeft',
                                                                                                    'ArrowRight',
                                                                                                    'Tab',
                                                                                                ];
                                                                                                if (
                                                                                                    !/^\d$/.test(e.key) && 
                                                                                                    !allowedKeys.includes(e.key)
                                                                                                ) {
                                                                                                    e.preventDefault();
                                                                                                }
                                                                                            }}
                                                                                            {...(splitBookingIds.has(booking.booking_id || booking.split_id)
                                                                                                ? { readOnly: true }
                                                                                                : { required: true })}
                                                                                        />
                                                                                    </div>
                                                                                    <div className="form-group mb-0">
                                                                                        <label className="required form-label">Small Box Quantity</label>
                                                                                        <input
                                                                                            type="text"
                                                                                            className="form-control"
                                                                                            value={splitData[bookingId]?.small_box_quantity ?? 0}
                                                                                            onChange={(e) => handleSplitInputChange(booking.booking_id || booking.split_id, 'small_box_quantity', e.target.value)}
                                                                                            onKeyDown={(e) => {
                                                                                                const allowedKeys = [
                                                                                                    'Backspace',
                                                                                                    'Delete',
                                                                                                    'ArrowLeft',
                                                                                                    'ArrowRight',
                                                                                                    'Tab',
                                                                                                ];
                                                                                                if (
                                                                                                    !/^\d$/.test(e.key) && 
                                                                                                    !allowedKeys.includes(e.key)
                                                                                                ) {
                                                                                                    e.preventDefault();
                                                                                                }
                                                                                            }}
                                                                                            {...(splitBookingIds.has(booking.booking_id || booking.split_id)
                                                                                                ? { readOnly: true }
                                                                                                : { required: true })}
                                                                                        />
                                                                                    </div>
                                                                                    {booking.payment_mode && (booking.payment_mode === 'Piece Rate Method' || booking.payment_mode === 'CBM Rate Method') && (
                                                                                        <div className="form-group mb-0">
                                                                                            <label className="required form-label">{getPaymentFieldLabel(booking.payment_mode)}</label>
                                                                                            <input
                                                                                                type="text"
                                                                                                className="form-control"
                                                                                                value={
                                                                                                    booking.payment_mode === 'Piece Rate Method'
                                                                                                        ? splitData[bookingId]?.total_pieces ?? ''
                                                                                                        : booking.payment_mode === 'CBM Rate Method'
                                                                                                            ? splitData[bookingId]?.cbm_value ?? ''
                                                                                                            : ''
                                                                                                }
                                                                                                onChange={(e) => {
                                                                                                    const inputValue = e.target.value;
                                                                                                    const isValid = /^\d*\.?\d*$/.test(inputValue);
                                                                                                    if (isValid || inputValue === '') {
                                                                                                        handleSplitInputChange(
                                                                                                            booking.booking_id || booking.split_id,
                                                                                                            (booking.payment_mode === 'Piece Rate Method'
                                                                                                                ? 'total_pieces'
                                                                                                                : booking.payment_mode === 'CBM Rate Method'
                                                                                                                    ? 'cbm_value'
                                                                                                                    : 'total_pieces') as keyof SplitData,
                                                                                                            inputValue
                                                                                                        );
                                                                                                    }
                                                                                                }}
                                                                                                {...(splitBookingIds.has(booking.booking_id || booking.split_id)
                                                                                                    ? { readOnly: true }
                                                                                                    : { required: true })}
                                                                                            />
                                                                                        </div>
                                                                                    )}
                                                                                </div>
                                                                            )}
                                                                            <div className="form-group mb-3">
                                                                                <label className="required form-label ">Notes</label>
                                                                                <textarea
                                                                                    className="form-control"
                                                                                    rows={2}
                                                                                    value={splitData[bookingId]?.notes || ''}
                                                                                    onChange={(e) => handleSplitInputChange(booking.booking_id || booking.split_id, 'notes', e.target.value)}
                                                                                    placeholder="Enter notes for split booking..."
                                                                                    {...(splitBookingIds.has(booking.booking_id || booking.split_id)
                                                                                        ? { readOnly: true }
                                                                                        : { required: true })}
                                                                                />
                                                                            </div>
                                                                            <div className="d-flex gap-2 justify-content-end ">
                                                                                {splitBookingIds.has(booking.booking_id || booking.split_id) ? (
                                                                                    <button
                                                                                        type="button"
                                                                                        className="btn btn-danger"
                                                                                        onClick={() => handleSplitCancel(booking)}
                                                                                        disabled={cancelingBookingIds.has(booking.booking_id || booking.split_id)}
                                                                                    >
                                                                                        {cancelingBookingIds.has(booking.booking_id || booking.split_id) ? (
                                                                                            <>
                                                                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                                                                Canceling...
                                                                                            </>
                                                                                        ) : (
                                                                                            'Cancel Split'
                                                                                        )}
                                                                                    </button>
                                                                                ) : (
                                                                                    <button
                                                                                        type="button"
                                                                                        className="btn btn-warning"
                                                                                        onClick={() => handleSplitClick(booking)}
                                                                                    >
                                                                                        Close
                                                                                    </button>
                                                                                )}
                                                                                <button
                                                                                    type="button"
                                                                                    className="btn btn-primary"
                                                                                    onClick={() => handleSplitSubmit(booking.booking_id || booking.split_id)}
                                                                                    disabled={
                                                                                        splitData[bookingId]?.totalQuantity > booking.total_quantity ||
                                                                                        splitBookingIds.has(booking.booking_id || booking.split_id) ||
                                                                                        loadingBookingIds.has(booking.booking_id || booking.split_id)
                                                                                    }
                                                                                >
                                                                                    {loadingBookingIds.has(booking.booking_id || booking.split_id) ? (
                                                                                        <>
                                                                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                                                            Processing...
                                                                                        </>
                                                                                    ) : (
                                                                                        'Submit'
                                                                                    )}
                                                                                </button>
                                                                            </div>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            )}
                                                            {openNotesForms.has(booking.booking_id || booking.split_id) && (
                                                                <tr>
                                                                    <td colSpan={7}>
                                                                        <div className="d-flex flex-column gap-3 p-3 bg-light">
                                                                            <div className="form-group mb-3">
                                                                                <label className="required form-label">Notes</label>
                                                                                <textarea
                                                                                    className="form-control"
                                                                                    rows={3}
                                                                                    value={notesData[booking.booking_id || booking.split_id] || ""}
                                                                                    onChange={(e) =>
                                                                                        setNotesData((prev) => ({
                                                                                            ...prev,
                                                                                            [booking.booking_id || booking.split_id]: e.target.value,
                                                                                        }))
                                                                                    }
                                                                                />
                                                                            </div>
                                                                            <div className="d-flex gap-2 justify-content-end">
                                                                                <button
                                                                                    type="button"
                                                                                    className="btn btn-secondary"
                                                                                    onClick={() =>
                                                                                        setOpenNotesForms((prev) => {
                                                                                            const newSet = new Set(prev);
                                                                                            newSet.delete(booking.booking_id || booking.split_id);
                                                                                            return newSet;
                                                                                        })
                                                                                    }
                                                                                >
                                                                                    Close
                                                                                </button>
                                                                                <button
                                                                                    type="button"
                                                                                    className="btn btn-primary"
                                                                                    onClick={() => handleNotesSubmit(booking.booking_id || booking.split_id)}
                                                                                    disabled={loadingNotes[booking.booking_id || booking.split_id]}
                                                                                >
                                                                                    {loadingNotes[booking.booking_id || booking.split_id] ? 'Submitting...' : 'Submit Notes'}
                                                                                </button>
                                                                            </div>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            )}
                                                        </>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>
                        {!isDropdownOpen && selectedBookingIds.length > 0 && (<div className="modal-footer">
                            <button
                                type="button"
                                className="btn btn-dark"
                                disabled={selectedBookingIds.length === 0}
                                onClick={() => {
                                    setShowaddbookingPopup(false);
                                    setAfterAddjourney(true)
                                    afterAddJourneshowdetails()
                                }}
                            >
                                Add Journey
                            </button>
                        </div>)}
                    </div>
                </div>
            </div>

            {/* Select All Confirmation Popup */}
            {showSelectAllConfirm && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 9999
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        padding: '30px',
                        maxWidth: '400px',
                        width: '90%',
                        textAlign: 'center',
                        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)'
                    }}>
                        <h5 className="mb-3">Confirm Select All</h5>
                        <p className="mb-4" style={{ color: '#666' }}>
                            Are you sure you want to select all available bookings? This will select {convertBookingsToOptions().filter(opt => opt.value !== SELECT_ALL_OPTION.value && !opt.isDisabled).length} bookings.
                        </p>
                        <div className="d-flex gap-3 justify-content-center">
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={() => setShowSelectAllConfirm(false)}
                                style={{ minWidth: '80px' }}
                            >
                                No
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={handleBulkSelectAll}
                                style={{ minWidth: '80px' }}
                            >
                                Yes
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Batch Operation Progress Modal */}
            {isBatchProcessing && (
                <div style={batchOperationModalStyles}>
                    <div style={batchOperationContentStyles}>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'center',
                            marginBottom: '24px',
                            height: '50px'
                        }}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                fontSize: '40px',
                                fontWeight: '700',
                                fontFamily: 'sans-serif',
                                color: '#262626',
                                overflow: 'hidden',
                                height: '50px'
                            }}>
                                {String(Math.round((batchProgress.current / batchProgress.total) * 100))
                                    .padStart(2, '0')
                                    .split('')
                                    .map((digit, index) => (
                                        <div
                                            key={index}
                                            style={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                height: '50px',
                                                width: '28px',
                                                animation: 'slideUp 1s ease-in-out',
                                                position: 'relative'
                                            }}
                                        >
                                            {[...Array(10)].map((_, i) => (
                                                <div
                                                    key={i}
                                                    style={{
                                                        position: 'absolute',
                                                        top: `${(i - parseInt(digit)) * 100}%`,
                                                        opacity: i === parseInt(digit) ? 1 : 0.1,
                                                        transition: 'top 0.3s ease, opacity 0.3s ease',
                                                    }}
                                                >
                                                    {i}
                                                </div>
                                            ))}
                                        </div>
                                    ))}
                                <span style={{ fontSize: '20px', marginLeft: '4px' }}>%</span>
                            </div>
                        </div>

                        <div className="text-center mb-3">
                            <p className="mb-0" style={{ color: '#666', fontSize: '14px' }}>
                                {batchProgress.current} of {batchProgress.total} {batchOperationType === 'select' ? 'bookings selected' : 'bookings removed'}
                            </p>
                            {successfulBatchIds.length > 0 && (
                                <p className="mb-0" style={{ color: '#28a745', fontSize: '12px' }}>
                                    Successful: {successfulBatchIds.length}
                                </p>
                            )}
                            {failedBatchIds.length > 0 && (
                                <p className="mb-0" style={{ color: '#dc3545', fontSize: '12px' }}>
                                    Failed: {failedBatchIds.length}
                                </p>
                            )}
                        </div>

                        <div className="mb-4">
                            <div
                                className="progress"
                                style={{
                                    height: '6px',
                                    backgroundColor: '#f0f0f0',
                                    borderRadius: '3px',
                                    overflow: 'hidden'
                                }}
                            >
                                <div
                                    className="progress-bar"
                                    role="progressbar"
                                    style={{
                                        width: `${Math.round((batchProgress.current / batchProgress.total) * 100)}%`,
                                        backgroundColor: batchOperationType === 'select' ? '#28a745' : '#C42A21',
                                        transition: 'width 0.3s ease-in-out',
                                        borderRadius: '3px'
                                    }}
                                    aria-valuenow={Math.round((batchProgress.current / batchProgress.total) * 100)}
                                    aria-valuemin={0}
                                    aria-valuemax={100}
                                />
                            </div>
                        </div>

                        <div className="text-center">
                            <p className="mb-2" style={{ color: '#666', fontSize: '14px' }}>
                                {batchOperationType === 'select' ? 'Please wait while we select all bookings...' : 'Please wait while we remove all bookings...'}
                            </p>
                            <p className="mb-3" style={{ color: batchOperationType === 'select' ? '#28a745' : '#C42A21', fontSize: '13px', fontWeight: '500' }}>
                                <strong>Do not close this window</strong>
                            </p>
                            
                            {/* <button
                                type="button"
                                className="btn btn-warning btn-sm"
                                onClick={() => setShowBatchCancelConfirm(true)}
                                style={{ marginTop: '10px' }}
                            >
                                Cancel Operation
                            </button> */}
                        </div>
                    </div>
                </div>
            )}

            {/* Batch Cancel Confirmation */}
            {showBatchCancelConfirm && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10000
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        padding: '25px',
                        maxWidth: '400px',
                        width: '90%',
                        textAlign: 'center',
                        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)'
                    }}>
                        {/* <h5 className="mb-3">Cancel Operation?</h5>
                        <p className="mb-4" style={{ color: '#666' }}>
                            Are you sure you want to cancel this operation? {batchProgress.current} out of {batchProgress.total} bookings have been processed.
                        </p> */}
                        <div className="d-flex gap-3 justify-content-center">
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={() => setShowBatchCancelConfirm(false)}
                                style={{ minWidth: '80px' }}
                            >
                                No, Continue
                            </button>
                            <button
                                type="button"
                                className="btn btn-danger"
                                onClick={cancelBatchOperation}
                                style={{ minWidth: '80px' }}
                            >
                                Yes, Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default ShipmentForm;


// import React, { useEffect, useState, useRef, useMemo } from 'react';
// import { toast, ToastContainer } from 'react-toastify';
// import { Link, Prompt, useHistory, useLocation } from 'react-router-dom';
// import Select, { components } from 'react-select';
// import { KTSVG } from '../../../_metronic/helpers';


// interface ShipmentFormProps {
//     onSubmit?: (formData: ShipmentFormData) => void;
// }


// interface ShipmentFormData {
//     name: string;
//     originating_hub: string;
//     destination_hub: string;
//     date_of_journey: string;
//     notes: string;
//     booking_id_list: string[];
// }

// interface Hub {
//     id: string;
//     name: string;
// }

// interface BookingID {
//     who_paid: any;
//     invoice_value: number;
//     nrml_payment_quantity: number;
//     cbm_value: any;
//     total_pieces: number;
//     notes: string;
//     id: string;
//     booking_id: string;
//     split_id: string;
//     total_quantity: number;
//     booking_mode: string;
//     consignor: string;
//     consignee: string;
//     payment_mode: string;
//     payment_status: string;
//     large_box_quantity?: number;
//     medium_box_quantity?: number;
//     small_box_quantity?: number;
//     piece_value?: number;
//     cbm_price?: number;
//     nrml_quantity_price?: number;
//     invoice_percentage?: number;
// }

// interface BookingDetails {
//     large_box_quantity: string;
//     medium_box_quantity: string;
//     small_box_quantity: string;
//     id: string;
//     booking_id: string;
//     split_id: string;
//     total_quantity: number;
//     booking_mode: string;
//     consignor: string;
//     consignee: string;
//     payment_mode: string;
//     payment_status: string;
//     large_journey_quantity?: number;
//     medium_journey_quantity?: number;
//     small_journey_quantity?: number;
//     shipment_status: string;
//     piece_value?: number;
//     cbm_price?: number;
//     nrml_quantity_price?: number;
//     invoice_percentage?: number;

// }

// interface SplitData {
//     invoice_value: number;
//     nrml_payment_quantity: number;
//     cbm_value: number;
//     total_pieces: number;
//     bookingId: string;
//     totalQuantity: number;
//     large_box_quantity?: number;
//     medium_box_quantity?: number;
//     small_box_quantity?: number;
//     piece_value?: number;
//     cbm_price?: number;
//     nrml_quantity_price?: number;
//     invoice_percentage?: number;
//     notes: string;
//     newBookingId?: string;
//     originalBookingId?: string;
//     isNewSplit?: boolean;
// }

// interface BookingOption {
//     value: string;
//     label: string;
//     isDisabled?: boolean;
//     booking: BookingID;
// }



// const ShipmentForm: React.FC<ShipmentFormProps> = ({ onSubmit }) => {


//     const [hubData, setHubData] = useState<Hub[]>([]);
//     const [bookingId, setBookingId] = useState<BookingID[]>([]);
//     const [showAddbookingpopup, setShowaddbookingPopup] = useState(false);
//     const [selectedBookingIds, setSelectedBookingIds] = useState<string[]>([]);
//     const [isDropdownOpen, setIsDropdownOpen] = useState(false);
//     const dropdownRef = useRef<HTMLDivElement>(null);
//     const [splitData, setSplitData] = useState<Record<string, SplitData>>({});
//     const [originalBookings, setOriginalBookings] = useState<Record<string, string>>({});
//     const [isSplitedresponseDone, setisSplitedresponseDone] = useState(false);
//     const [issplitopen, setissplitopen] = useState(false);
//     const [iscancelOpen, setisCancelopen] = useState(false);
//     const [afterAddjourney, setAfterAddjourney] = useState(false);
//     const [Bokkingdetails, setBokkingdetails] = useState<BookingDetails[]>([]);
//     const [loading, setLoading] = useState(false)
//     const [splitBookingIds, setSplitBookingIds] = useState<Set<string>>(new Set());
//     const [allSelectedBookingIds, setAllSelectedBookingIds] = useState<string[]>([]);
//     const [openSplitForms, setOpenSplitForms] = useState<Set<string>>(new Set());
//     const [selectedOptions, setSelectedOptions] = useState<BookingOption[]>([]);
//     const [addedBookingIds, setAddedBookingIds] = useState<string[]>([]);
//     const [notesData, setNotesData] = useState<Record<string, string>>({});
//     const [loadingNotes, setLoadingNotes] = useState<Record<string, boolean>>({});
//     const [openNotesForms, setOpenNotesForms] = useState<Set<string>>(new Set());
//     const [cancelingBookingIds, setCancelingBookingIds] = useState<Set<string>>(new Set());
//     const [loadingBookingIds, setLoadingBookingIds] = useState<Set<string>>(new Set());
//     const [isBulkRemoving, setIsBulkRemoving] = useState(false);
//     const [bulkRemoveProgress, setBulkRemoveProgress] = useState({ current: 0, total: 0 });
//     const [formData, setFormData] = useState<ShipmentFormData>({
//         name: '',
//         originating_hub: '',
//         destination_hub: '',
//         date_of_journey: '',
//         notes: '',
//         booking_id_list: []
//     });
//     const [isUnloadingBlocked, setIsUnloadingBlocked] = useState(false);
//     // Add these with your existing state variables
//     const [isBulkSelecting, setIsBulkSelecting] = useState(false);
//     const [showSelectAllConfirm, setShowSelectAllConfirm] = useState(false);
//     const [bulkSelectProgress, setBulkSelectProgress] = useState({ current: 0, total: 0 });


//     const handleBulkSelectAll = async () => {
//         setShowSelectAllConfirm(false);

//         // Get all available booking options
//         const allOptions = convertBookingsToOptions()
//             .filter(opt => opt.value !== SELECT_ALL_OPTION.value && !opt.isDisabled);

//         // Calculate how many bookings need to be selected
//         const idsToAdd = allOptions
//             .map(opt => opt.value)
//             .filter(id => !selectedBookingIds.includes(id));

//         if (idsToAdd.length === 0) {
//             return; // Nothing to select
//         }

//         // Show bulk selection modal
//         setIsBulkSelecting(true);
//         setBulkSelectProgress({ current: 0, total: idsToAdd.length });
//         setIsUnloadingBlocked(true);

//         let completedCount = 0;

//         // Process each booking one by one to show proper progress
//         for (let i = 0; i < idsToAdd.length; i++) {
//             const id = idsToAdd[i];

//             try {
//                 // Update the booking status via API
//                 await makeApiCall("https://backendcway.causewaycargo.com/superadmin/updatebookingstatus", "PUT", {
//                     booking_id: id,
//                     checked_status: "Yes"
//                 });

//                 // Update selected booking IDs state
//                 setSelectedBookingIds(prev => {
//                     if (!prev.includes(id)) {
//                         return [...prev, id];
//                     }
//                     return prev;
//                 });

//                 // Update progress
//                 completedCount++;
//                 setBulkSelectProgress({ current: completedCount, total: idsToAdd.length });

//             } catch (error) {
//                 console.error(`Error selecting booking ${id}:`, error);
//                 completedCount++;
//                 setBulkSelectProgress({ current: completedCount, total: idsToAdd.length });
//             }
//         }

//         // Hide modal and reset states
//         setIsUnloadingBlocked(false);
//         setIsBulkSelecting(false);
//         setBulkSelectProgress({ current: 0, total: 0 });
//     };


//     const history = useHistory();

//     const SELECT_ALL_OPTION: BookingOption = {
//         value: "Select All",
//         label: "Select All",
//         booking: {} as BookingID,
//     };

//     const selectedBookingIdsRef = useRef(selectedBookingIds);
//     const addedBookingIdsRef = useRef(addedBookingIds);

//     useEffect(() => {
//         addedBookingIdsRef.current = addedBookingIds;
//     }, [addedBookingIds]);

//     useEffect(() => {
//         selectedBookingIdsRef.current = selectedBookingIds;
//     }, [selectedBookingIds]);

//     // Function to send API request
//     const sendCancelRequest = () => {
//         const bookingIdsToSend = [
//             ...addedBookingIdsRef.current,
//             ...selectedBookingIdsRef.current,
//         ];
//         const uniqueBookingIds = Array.from(new Set(bookingIdsToSend));

//         if (uniqueBookingIds.length > 0) {
//             fetch(
//                 'https://backendcway.causewaycargo.com/superadmin/afteraddjourneycancel',
//                 {
//                     method: 'POST',
//                     headers: {
//                         auth: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6Nywicm9sZSI6InN1cGVyX2FkbWluIiwiaWF0IjoxNzI2ODM0MzIwfQ.edXyvu0znbVoi5KU3iL8FPww3qe8LMU0oXOCwdZi8do',
//                         'Content-Type': 'application/json',
//                     },
//                     body: JSON.stringify({ booking_id_list: uniqueBookingIds }),
//                     keepalive: true,
//                 }
//             ).catch((error) => {
//                 console.error('Error sending cancel request:', error);
//             });
//         }
//     };

//     // Warn on browser/tab close
//     // useEffect(() => {
//     //     const handleBeforeUnload = (event: BeforeUnloadEvent) => {
//     //         const bookingIdsToSend = [
//     //             ...addedBookingIdsRef.current,
//     //             ...selectedBookingIdsRef.current,
//     //         ];
//     //         if (bookingIdsToSend.length > 0) {
//     //             event.preventDefault();
//     //             event.returnValue = 'Are you sure you want to leave?';
//     //             sendCancelRequest();
//     //         }
//     //     };

//     //     window.addEventListener('beforeunload', handleBeforeUnload);
//     //     return () => {
//     //         window.removeEventListener('beforeunload', handleBeforeUnload);
//     //     };
//     // }, []);


//     useEffect(() => {
//         const handleBeforeUnload = (event: BeforeUnloadEvent) => {
//             const bookingIdsToSend = [
//                 ...addedBookingIdsRef.current,
//                 ...selectedBookingIdsRef.current,
//             ];

//             // Block if there are pending bookings OR bulk removal is in progress
//             if (bookingIdsToSend.length > 0 || isUnloadingBlocked) {
//                 event.preventDefault();
//                 event.returnValue = 'Are you sure you want to leave? Changes may not be saved.';

//                 // If bulk removal is in progress, send cancel request
//                 if (isUnloadingBlocked) {
//                     sendCancelRequest();
//                 }
//             }
//         };

//         window.addEventListener('beforeunload', handleBeforeUnload);
//         return () => {
//             window.removeEventListener('beforeunload', handleBeforeUnload);
//         };
//     }, [isUnloadingBlocked]); // Add dependency

//     // Internal routing check (Prompt)
//     const location = useLocation();

//     const shouldBlockNavigation =
//         location.pathname === '/shipment/shipmentform' &&
//         (selectedBookingIdsRef.current.length > 0 ||
//             addedBookingIdsRef.current.length > 0);

//     const convertBookingsToOptions = (): BookingOption[] => {
//         const bookingOptions = bookingId.map(booking => {
//             const id = booking.split_id || booking.booking_id;
//             const isDisabled = splitBookingIds.has(id);

//             return {
//                 value: id,
//                 label: id,
//                 isDisabled,
//                 booking
//             };
//         });

//         return [SELECT_ALL_OPTION, ...bookingOptions];
//     };

//     useEffect(() => {
//         const options = selectedBookingIds.map(id => {
//             const booking = getBookingDetails(id);
//             return booking ? {
//                 value: id,
//                 label: id,
//                 booking: booking,
//                 isDisabled: splitBookingIds.has(id)
//             } : null;
//         }).filter(Boolean) as BookingOption[];

//         setSelectedOptions(options);
//     }, [selectedBookingIds, splitBookingIds]);



//     useEffect(() => {
//         fetchHubdropdown();
//     }, []);


//     const updatedBookingIds = useMemo(() => {
//         return Array.from(new Set([...addedBookingIds, ...selectedBookingIds]));
//     }, [addedBookingIds, selectedBookingIds]);

//     // Add this effect AFTER the declaration of updatedBookingIds
//     useEffect(() => {
//         setAllSelectedBookingIds(updatedBookingIds);
//     }, [updatedBookingIds]);

//     useEffect(() => {
//         setFormData(prevFormData => ({
//             ...prevFormData,
//             booking_id_list: updatedBookingIds
//         }));
//     }, [updatedBookingIds]);

//     const handleSubmit = async (e: React.FormEvent) => {
//         setLoading(true)
//         e.preventDefault();
//         try {
//             const response = await makeApiCall(
//                 "https://backendcway.causewaycargo.com/superadmin/addshipment",
//                 "POST",
//                 formData
//             );

//             if (response?.status === "success") {
//                 toast.success(response.message || "Shipment added successfully!");
//                 setAfterAddjourney(false)
//                 setSelectedBookingIds([])
//                 setFormData({
//                     name: '',
//                     originating_hub: '',
//                     destination_hub: '',
//                     date_of_journey: '',
//                     notes: '',
//                     booking_id_list: []
//                 });

//                 // ✅ Clear the refs to prevent unload trigger
//                 selectedBookingIdsRef.current = [];
//                 addedBookingIdsRef.current = [];

//                 setTimeout(() => {
//                     history.push('/shipment');

//                 }, 2500);
//                 console.log("Response:", response);
//                 onSubmit?.(formData);
//                 setLoading(false)

//             }
//         } catch (error: any) {
//             toast.error(error.message || "Error adding shipment");
//             console.error("Error:", error);
//         }
//     };

//     const handleInputChange = (field: keyof ShipmentFormData, value: string) => {
//         setFormData(prev => {
//             if (
//                 (field === "originating_hub" && value === prev.destination_hub) ||
//                 (field === "destination_hub" && value === prev.originating_hub)
//             ) {
//                 toast.error("Originating hub and Destination hub cannot be the same.");
//                 return prev;
//             }

//             return {
//                 ...prev,
//                 [field]: value
//             };
//         });
//     };

//     const handleBookingSelection = async (bookingIdentifier: string) => {
//         console.log("triggered handleBookingSelection")

//         setSelectedBookingIds((prev) => {
//             const isCurrentlySelected = prev.includes(bookingIdentifier);
//             const updatedIds = isCurrentlySelected
//                 ? prev.filter(id => id !== bookingIdentifier)
//                 : [...prev, bookingIdentifier];

//             const checkedStatus = isCurrentlySelected ? "No" : "Yes";
//             console.log("checkedStatus", checkedStatus)

//             makeApiCall("https://backendcway.causewaycargo.com/superadmin/updatebookingstatus", "PUT", {
//                 booking_id: bookingIdentifier,
//                 checked_status: checkedStatus
//             })
//                 .then(response => {
//                     if (response?.status !== "success") {
//                         setSelectedBookingIds(prev);
//                         toast.error(`Failed to update status for ${bookingIdentifier}`);
//                     }
//                 })
//                 .catch(error => {
//                     setSelectedBookingIds(prev);
//                     console.error(`Error updating booking ${bookingIdentifier} status:`, error);
//                     toast.error(`Failed to update status for ${bookingIdentifier}`);
//                 });

//             return updatedIds;
//         });
//     };

//     const removeBooking = async (id: string) => {
//         // Don't allow removal if it's a split booking ID
//         if (splitBookingIds.has(id)) {
//             toast.error(`Booking ${id} has been split and cannot be removed.`);
//             return;
//         }

//         // Update the selectedBookingIds
//         setSelectedBookingIds(prev => prev.filter(selectedId => selectedId !== id));

//         try {
//             const response = await makeApiCall("https://backendcway.causewaycargo.com/superadmin/updatebookingstatus", "PUT", {
//                 booking_id: id,
//                 checked_status: "No"
//             });

//             if (response?.status === "success") {
//                 setSplitData(prev => {
//                     const newData = { ...prev };
//                     delete newData[id];
//                     return newData;
//                 });

//             } else {
//                 console.error("Failed to update booking status");
//                 toast.error(`Failed to remove booking ${id}`);
//             }
//         } catch (error) {
//             console.error("Error updating booking status:", error);
//             toast.error(`Error removing booking ${id}`);
//         }
//     };

//     const getBookingDetails = (id: string) => {
//         return bookingId.find(booking =>
//             (booking.booking_id === id || booking.split_id === id) &&
//             (!booking.split_id || booking.split_id === id)
//         );
//     };
//     const fetchHubdropdown = async () => {
//         try {
//             const response = await fetch("https://backendcway.causewaycargo.com/superadmin/getallhublist", {
//                 method: 'GET',
//                 headers: {
//                     'auth': 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6OCwicm9sZSI6InN1cGVyX2FkbWluIiwiaWF0IjoxNzQxMTUwMjI0fQ.BoQy0I28tpGeO2eL70pTX0iqn8mXpnJ27GExnpOka6o',
//                     'Content-Type': 'application/json',
//                 },
//             });
//             const result = await response.json();
//             setHubData(result?.data);
//         } catch (error) {
//             console.error("Error fetching hub list:", error);
//         }
//     };

//     const fetchAllbookingidforship = async () => {

//         try {
//             const response = await fetch("https://backendcway.causewaycargo.com/superadmin/allbookingforship", {
//                 method: 'GET',
//                 headers: {
//                     'auth': 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6OCwicm9sZSI6InN1cGVyX2FkbWluIiwiaWF0IjoxNzQxMTUwMjI0fQ.BoQy0I28tpGeO2eL70pTX0iqn8mXpnJ27GExnpOka6o', // Replace with actual auth token
//                     'Content-Type': 'application/json',
//                 },
//             });
//             const result = await response.json();

//             const normalizedBookings = result?.data.map((booking: BookingID) => ({
//                 ...booking,
//                 uniqueId: booking.split_id || booking.booking_id
//             }));

//             setBookingId(normalizedBookings);
//         } catch (error) {
//             console.error("Error fetching booking list:", error);
//             toast.error("Failed to fetch booking list");
//         }
//     };

//     const afterAddJourneshowdetails = async () => {
//         if (selectedBookingIds.length === 0) {
//             console.warn("No bookings selected for the journey.");
//             return;
//         }

//         try {
//             const bookingIdsToSend = [...addedBookingIds, ...selectedBookingIds];
//             const uniqueBookingIds = Array.from(new Set(bookingIdsToSend));

//             const response = await fetch("https://backendcway.causewaycargo.com/superadmin/addjourneybookinglist", {
//                 method: 'POST',
//                 headers: {
//                     'auth': 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6OCwicm9sZSI6InN1cGVyX2FkbWluIiwiaWF0IjoxNzQxMTUwMjI0fQ.BoQy0I28tpGeO2eL70pTX0iqn8mXpnJ27GExnpOka6o', // Replace with actual auth token
//                     'Content-Type': 'application/json',
//                 },
//                 body: JSON.stringify({
//                     booking_id_list: uniqueBookingIds
//                 })
//             });

//             const result = await response.json();

//             if (response.ok) {
//                 setAddedBookingIds(prev => {
//                     const newAddedIds = [...prev, ...selectedBookingIds];
//                     return Array.from(new Set(newAddedIds));
//                 });
//                 setBokkingdetails(result?.data)
//                 setSelectedBookingIds([]);
//                 setOpenSplitForms(new Set());
//                 setSplitData({});
//                 return true;
//             } else {
//                 console.error("Failed to add journey:", result);
//                 return false;
//             }
//         } catch (error) {
//             console.error("Error adding journey:", error);
//             return false;
//         }
//     };

//     const afterAddJournyCancel = async () => {
//         if (updatedBookingIds.length === 0) {
//             console.warn("No bookings selected for cancellation.");
//             return;
//         }

//         try {
//             const bookingIdsToSend = [...addedBookingIds, ...selectedBookingIds];
//             const uniqueBookingIds = Array.from(new Set(bookingIdsToSend));

//             const response = await fetch("https://backendcway.causewaycargo.com/superadmin/afteraddjourneycancel", {
//                 method: 'POST',
//                 headers: {
//                     'auth': 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6OCwicm9sZSI6InN1cGVyX2FkbWluIiwiaWF0IjoxNzQxMTUwMjI0fQ.BoQy0I28tpGeO2eL70pTX0iqn8mXpnJ27GExnpOka6o',
//                     'Content-Type': 'application/json',
//                 },
//                 body: JSON.stringify({
//                     booking_id_list: uniqueBookingIds
//                 })
//             });

//             const result = await response.json();

//             if (response.ok) {

//                 setAddedBookingIds([]);
//                 setSelectedBookingIds([]);
//                 setAfterAddjourney(false);

//                 return true;
//             } else {
//                 console.error("Failed to cancel journey:", result);
//                 return false;
//             }
//         } catch (error) {
//             console.error("Error cancelling journey:", error);
//             return false;
//         }
//     };


//     // const updatedBookingIds = useMemo(() => {
//     //     return Array.from(new Set([...addedBookingIds, ...selectedBookingIds]));
//     // }, [addedBookingIds, selectedBookingIds]);



//     const makeApiCall = async (url: string, method: string, body: any) => {
//         try {
//             const response = await fetch(url, {
//                 method,
//                 headers: {
//                     'auth': 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6Nywicm9sZSI6InN1cGVyX2FkbWluIiwiaWF0IjoxNzI2ODM0MzIwfQ.edXyvu0znbVoi5KU3iL8FPww3qe8LMU0oXOCwdZi8do',
//                     'Content-Type': 'application/json',
//                 },
//                 body: JSON.stringify(body)
//             });
//             return await response.json();
//         } catch (error) {
//             console.error("API Error:", error);
//             throw error;
//         }
//     };

//     const handleNormalSplit = async (bookingData: BookingID, data: SplitData) => {
//         try {
//             if (!bookingData.booking_id && !bookingData.split_id) {
//                 throw new Error("Invalid booking: No booking_id or split_id found");
//             }

//             if (!data.totalQuantity ||
//                 data.totalQuantity <= 0 ||
//                 data.totalQuantity >= bookingData.total_quantity) {
//                 alert(`Total quantity must be between 1 and ${bookingData.total_quantity - 1}`);
//                 return;
//             }

//             const isAlreadySplit = (bookingData.split_id && bookingData.split_id.includes('-')) ||
//                 (bookingData.booking_id && bookingData.booking_id.includes('-'));

//             const currentBookingId = bookingData.booking_id || bookingData.split_id;

//             const response = await makeApiCall(
//                 "https://backendcway.causewaycargo.com/superadmin/normalsplitbooking",
//                 "POST",
//                 {
//                     booking_id: currentBookingId,
//                     journey_quantity: Number(data.totalQuantity),
//                     common_method_value: Number(
//                         data.total_pieces ||
//                         data.cbm_value ||
//                         0
//                     ),
//                     split_notes: data.notes
//                 }
//             );

//             if (response?.status === "success") {
//                 const updateData = isAlreadySplit ? response.data : response.insert_data;

//                 if (!updateData) {
//                     throw new Error("Invalid response: Missing required data");
//                 }

//                 const newSplitId = updateData.split_id || (isAlreadySplit ? currentBookingId : null);

//                 if (!newSplitId) {
//                     throw new Error("Invalid split ID received from server");
//                 }

//                 setSelectedBookingIds(prev => {
//                     const updatedIds = prev.filter(id => id !== currentBookingId);
//                     return [...updatedIds, newSplitId];
//                 });

//                 const updatedBooking = {
//                     ...bookingData,
//                     booking_id: "",
//                     split_id: newSplitId,
//                     total_quantity: Number(updateData.total_quantity),
//                     notes: updateData.notes,
//                     total_pieces: updateData.total_pieces,
//                     cbm_value: updateData.cbm_value
//                 };

//                 setSelectedOptions(prev => {
//                     const updatedOptions = prev.filter(opt => opt.value !== currentBookingId);
//                     return [...updatedOptions, {
//                         value: newSplitId,
//                         label: newSplitId,
//                         booking: updatedBooking,
//                         isDisabled: false
//                     }];
//                 });

//                 setBookingId(prev => {
//                     return prev.map(booking => {
//                         if (booking.booking_id === currentBookingId || booking.split_id === currentBookingId) {
//                             return updatedBooking;
//                         }
//                         return booking;
//                     });
//                 });

//                 setSplitBookingIds(prev => {
//                     const newSet = new Set(prev);
//                     newSet.add(newSplitId);
//                     return newSet;
//                 });

//                 if (!isAlreadySplit) {
//                     setOriginalBookings(prev => ({
//                         ...prev,
//                         [newSplitId]: currentBookingId
//                     }));
//                 }

//                 setAllSelectedBookingIds(prev => {
//                     const updatedIds = prev.filter(id => id !== currentBookingId);
//                     return [...updatedIds, newSplitId];
//                 });

//                 setSplitData(prev => {
//                     const newData = { ...prev };
//                     delete newData[currentBookingId];
//                     return newData;
//                 });

//                 setOpenSplitForms(prev => {
//                     const newSet = new Set(prev);
//                     newSet.delete(currentBookingId);
//                     return newSet;
//                 });

//                 setissplitopen(false);
//                 setisCancelopen(true);

//                 alert("Booking split successfully!");
//             } else {
//                 throw new Error("Split booking failed: " + response?.message);
//             }
//         } catch (error) {
//             console.error("Normal split error:", error);
//             alert("Failed to split booking: " + (error instanceof Error ? error.message : "Unknown error"));
//         }
//     };


//     const handleBoxSplit = async (bookingData: BookingID, data: SplitData) => {
//         try {
//             if (!bookingData.booking_id && !bookingData.split_id) {
//                 throw new Error("Invalid booking: No booking_id or split_id found");
//             }

//             console.log("bookingData", bookingData);

//             const largeBoxQty = Number(data.large_box_quantity || 0);
//             const mediumBoxQty = Number(data.medium_box_quantity || 0);
//             const smallBoxQty = Number(data.small_box_quantity || 0);
//             const totalPieces = Number(data.total_pieces || 0);
//             const originalTotalPieces = Number(bookingData.total_pieces || 0);

//             // Validation checks
//             if (largeBoxQty === 0 && mediumBoxQty === 0 && smallBoxQty === 0) {
//                 alert("Error: At least one box quantity must be greater than zero.");
//                 return;
//             }

//             if (
//                 largeBoxQty === Number(bookingData.large_box_quantity || 0) &&
//                 mediumBoxQty === Number(bookingData.medium_box_quantity || 0) &&
//                 smallBoxQty === Number(bookingData.small_box_quantity || 0)
//             ) {
//                 alert("Error: At least one box quantity must change.");
//                 return;
//             }


//             const isAlreadySplit = (bookingData.split_id && bookingData.split_id.includes('-')) ||
//                 (bookingData.booking_id && bookingData.booking_id.includes('-'));

//             const currentBookingId = bookingData.booking_id || bookingData.split_id;

//             const response = await makeApiCall(
//                 "https://backendcway.causewaycargo.com/superadmin/addsplitbooking",
//                 "POST",
//                 {
//                     booking_id: currentBookingId,
//                     large_quantity: largeBoxQty,
//                     medium_quantity: mediumBoxQty,
//                     small_quantity: smallBoxQty,
//                     common_method_value: Number(data.total_pieces || data.cbm_value || 0),
//                     split_notes: data.notes
//                 }
//             );

//             if (response?.status === "success") {
//                 const updateData = isAlreadySplit ? response.data : response.insert_data;

//                 if (!updateData) {
//                     throw new Error("Invalid response: Missing required data");
//                 }

//                 const newSplitId = updateData.split_id || (isAlreadySplit ? currentBookingId : null);

//                 if (!newSplitId) {
//                     throw new Error("Invalid split ID received from server");
//                 }

//                 const updatedBooking = {
//                     ...bookingData,
//                     booking_id: "",
//                     split_id: newSplitId,
//                     large_box_quantity: Number(updateData.large_box_quantity || 0),
//                     medium_box_quantity: Number(updateData.medium_box_quantity || 0),
//                     small_box_quantity: Number(updateData.small_box_quantity || 0),
//                     total_quantity: Number(updateData.total_quantity),
//                     total_pieces: updateData.total_pieces,
//                     cbm_value: updateData.cbm_value,
//                     notes: updateData.notes
//                 };

//                 setSelectedBookingIds(prev => {
//                     const updatedIds = prev.filter(id => id !== currentBookingId);
//                     return [...updatedIds, newSplitId];
//                 });

//                 setSelectedOptions(prev => {
//                     const updatedOptions = prev.filter(opt => opt.value !== currentBookingId);
//                     return [...updatedOptions, {
//                         value: newSplitId,
//                         label: newSplitId,
//                         booking: updatedBooking,
//                         isDisabled: false
//                     }];
//                 });

//                 setBookingId(prev => {
//                     return prev.map(booking => {
//                         if (booking.booking_id === currentBookingId || booking.split_id === currentBookingId) {
//                             return updatedBooking;
//                         }
//                         return booking;
//                     });
//                 });

//                 setSplitBookingIds(prev => {
//                     const newSet = new Set(prev);
//                     newSet.add(newSplitId);
//                     return newSet;
//                 });

//                 if (!isAlreadySplit) {
//                     setOriginalBookings(prev => ({
//                         ...prev,
//                         [newSplitId]: currentBookingId
//                     }));
//                 }

//                 setAllSelectedBookingIds(prev => {
//                     const updatedIds = prev.filter(id => id !== currentBookingId);
//                     return [...updatedIds, newSplitId];
//                 });

//                 setSplitData(prev => {
//                     const newData = { ...prev };
//                     delete newData[currentBookingId];
//                     return newData;
//                 });

//                 setOpenSplitForms(prev => {
//                     const newSet = new Set(prev);
//                     newSet.delete(currentBookingId);
//                     return newSet;
//                 });

//                 setissplitopen(false);
//                 setisCancelopen(true);

//                 alert("Booking split successfully!");
//             } else {
//                 throw new Error("Split booking failed: " + response?.message);
//             }
//         } catch (error) {
//             console.error("Box split error:", error);
//             alert("Failed to split booking: " + (error instanceof Error ? error.message : "Unknown error"));
//         }
//     };

//     const handleSplitSubmit = async (bookingId: string) => {
//         const data = splitData[bookingId];
//         const booking = getBookingDetails(bookingId);
//         if (!data || !booking) return;
//         const whoPaid = booking.who_paid;

//         setLoadingBookingIds(prev => {
//             const newSet = new Set(prev);
//             newSet.add(bookingId);
//             return newSet;
//         });

//         try {
//             if (booking.booking_mode === 'normal booking') {
//                 const totalQty = data.totalQuantity;
//                 const defaultTotalQty = booking.total_quantity;

//                 const paymentMethod = booking.payment_mode;


//                 const totalPieces = Number(data.total_pieces || 0);
//                 const originalTotalPieces = Number(booking.total_pieces || 0);

//                 const cbmValue = Number(data.cbm_value || 0);
//                 const originalCBMValue = Number(booking.cbm_value || 0);


//                 if (!totalQty || totalQty <= 0 || totalQty > defaultTotalQty || totalQty === defaultTotalQty) {
//                     alert("Please enter a valid total quantity");
//                     return;
//                 }
//                 if (whoPaid === "Consignor" && (booking.payment_mode === "Piece Rate Method" || booking.payment_mode === "CBM Rate Method")) {
//                     let isValidChange = false;

//                     if (paymentMethod === "Piece Rate Method") {
//                         if (totalPieces < originalTotalPieces) {
//                             isValidChange = true;
//                         } else {
//                             alert("Error: Total pieces must be reduced and not remain the same.");
//                             return;
//                         }
//                     } else if (paymentMethod === "CBM Rate Method") {
//                         if (cbmValue < originalCBMValue) {
//                             isValidChange = true;
//                         } else {
//                             alert("Error: CBM value must be reduced and not remain the same.");
//                             return;
//                         }
//                     }

//                     if (!isValidChange) {
//                         alert("Error: Either total pieces (Piece Rate) or CBM value (CBM Rate) must be reduced.");
//                         return;
//                     }

//                     // ✅ Ensure box quantity does not exceed original values
//                     if (
//                         totalQty > defaultTotalQty
//                     ) {
//                         alert("Error: Box quantities cannot exceed original values.");
//                         return;
//                     }
//                 }

//                 await handleNormalSplit(booking, data);
//             } else {
//                 const largeQty = Number(data.large_box_quantity || 0);
//                 const mediumQty = Number(data.medium_box_quantity || 0);
//                 const smallQty = Number(data.small_box_quantity || 0);

//                 const originalLargeQty = Number(booking.large_box_quantity || 0);
//                 const originalMediumQty = Number(booking.medium_box_quantity || 0);
//                 const originalSmallQty = Number(booking.small_box_quantity || 0);

//                 const totalPieces = Number(data.total_pieces || 0);
//                 const originalTotalPieces = Number(booking.total_pieces || 0);

//                 const cbmValue = Number(data.cbm_value || 0);
//                 const originalCBMValue = Number(booking.cbm_value || 0);

//                 const paymentMethod = booking.payment_mode; // Ensure payment method is considered

//                 // ✅ Ensure at least one box quantity is reduced
//                 const isBoxQuantitySame = (
//                     largeQty === originalLargeQty &&
//                     mediumQty === originalMediumQty &&
//                     smallQty === originalSmallQty
//                 );

//                 if (isBoxQuantitySame) {
//                     alert("Error: At least one box quantity must be reduced.");
//                     return;
//                 }

//                 if (whoPaid === "Consignor" && (booking.payment_mode === "Piece Rate Method" || booking.payment_mode === "CBM Rate Method")) {
//                     let isValidChange = false;

//                     if (paymentMethod === "Piece Rate Method") {
//                         if (totalPieces < originalTotalPieces) {
//                             isValidChange = true;
//                         } else {
//                             alert("Error: Total pieces must be reduced and not remain the same.");
//                             return;
//                         }
//                     } else if (paymentMethod === "CBM Rate Method") {
//                         if (cbmValue < originalCBMValue) {
//                             isValidChange = true;
//                         } else {
//                             alert("Error: CBM value must be reduced and not remain the same.");
//                             return;
//                         }
//                     }

//                     if (!isValidChange) {
//                         alert("Error: Either total pieces (Piece Rate) or CBM value (CBM Rate) must be reduced.");
//                         return;
//                     }

//                     // ✅ Ensure box quantity does not exceed original values
//                     if (
//                         largeQty > originalLargeQty ||
//                         mediumQty > originalMediumQty ||
//                         smallQty > originalSmallQty
//                     ) {
//                         alert("Error: Box quantities cannot exceed original values.");
//                         return;
//                     }
//                 }

//                 await handleBoxSplit(booking, data);
//             }
//             setOpenSplitForms(prev => {
//                 const newSet = new Set(prev);
//                 newSet.delete(bookingId);
//                 return newSet;
//             });
//         } catch (error) {
//             console.error("Split submission error:", error);
//         } finally {
//             // Clear loading state regardless of success or failure
//             setLoadingBookingIds(prev => {
//                 const newSet = new Set(prev);
//                 newSet.delete(bookingId);
//                 return newSet;
//             });
//         }
//     };


//     const handleSplitCancel = async (booking: BookingID) => {
//         if (!window.confirm("Are you sure you want to cancel this split?")) {
//             return;
//         }

//         const currentId = booking.split_id || booking.booking_id;
//         console.log("currentId", currentId)
//         if (!currentId) return;

//         // Set loading state for this specific booking ID
//         setCancelingBookingIds(prev => {
//             const newSet = new Set(prev);
//             newSet.add(currentId);
//             return newSet;
//         });

//         let originalId = originalBookings[currentId];

//         if (!originalId && currentId.includes('-')) {
//             originalId = currentId
//         }

//         try {
//             const response = await makeApiCall(
//                 "https://backendcway.causewaycargo.com/superadmin/cancelsplit",
//                 "POST",
//                 {
//                     old_booking_id: originalId,
//                     new_booking_id: currentId,
//                     booking_id_list: [originalId, currentId]
//                 }
//             );

//             if (response?.status === "success") {
//                 setSelectedBookingIds(prev => {
//                     const withoutCurrent = prev.filter(id => id !== currentId);
//                     if (!withoutCurrent.includes(originalId)) {
//                         withoutCurrent.push(originalId);
//                     }
//                     return Array.from(new Set(withoutCurrent));
//                 });

//                 const updatedOriginalBooking = {
//                     ...booking,
//                     booking_id: originalId,
//                     split_id: "",
//                     total_quantity: response.data?.total_quantity || booking.total_quantity,
//                     large_box_quantity: response.data?.large_box_quantity,
//                     medium_box_quantity: response.data?.medium_box_quantity,
//                     small_box_quantity: response.data?.small_box_quantity,
//                     total_pieces: response.data?.total_pieces,
//                     cbm_value: response.data?.cbm_value,
//                     notes: response.data?.notes
//                 };

//                 setSelectedOptions(prev => {
//                     const filteredOptions = prev.filter(opt => opt.value !== currentId);
//                     const originalOption = {
//                         value: originalId,
//                         label: originalId,
//                         booking: updatedOriginalBooking,
//                         isDisabled: false
//                     };

//                     return [...filteredOptions, originalOption];
//                 });
//                 setBookingId(prev => {
//                     return prev.map(b => {
//                         if (b.split_id === currentId || b.booking_id === originalId) {
//                             return updatedOriginalBooking;
//                         }
//                         return b;
//                     });
//                 });

//                 setSplitBookingIds(prev => {
//                     const newSet = new Set(prev);
//                     newSet.delete(currentId);
//                     return newSet;
//                 });

//                 setOpenSplitForms(prev => {
//                     const newSet = new Set(prev);
//                     newSet.delete(currentId);
//                     return newSet;
//                 });

//                 setOriginalBookings(prev => {
//                     const newData = { ...prev };
//                     delete newData[currentId];
//                     return newData;
//                 });

//                 setSplitData(prev => {
//                     const newData = { ...prev };
//                     delete newData[currentId];
//                     return newData;
//                 });

//                 setAllSelectedBookingIds(prev => {
//                     const filtered = prev.filter(id => id !== currentId);
//                     if (!filtered.includes(originalId)) {
//                         filtered.push(originalId);
//                     }
//                     return filtered;
//                 });

//                 alert("Split cancelled successfully!");
//             }
//         } catch (error) {
//             console.error("Cancel split error:", error);
//             alert("Failed to cancel split");
//         } finally {
//             // Clear loading state regardless of success or failure
//             setCancelingBookingIds(prev => {
//                 const newSet = new Set(prev);
//                 newSet.delete(currentId);
//                 return newSet;
//             });
//         }
//     };

//     const getPaymentFieldLabel = (paymentMode: string): string => {
//         switch (paymentMode) {
//             case 'Piece Rate Method':
//                 return 'Piece Value';
//             case 'CBM Rate Method':
//                 return 'CBM Value';
//             case 'Normal Unit Price Method':
//             case 'Splitted Unit Price Method':
//                 return 'Unit Price Value';
//             case 'Invoice Value Method':
//                 return 'Invoice Value';
//             default:
//                 return '';
//         }
//     };

//     const handleSplitInputChange = (bookingId: string, field: keyof SplitData, value: string) => {
//         console.log(`Updating ${field} for booking ${bookingId} to:`, value);

//         setSplitData(prev => {
//             const updatedBooking = {
//                 ...prev[bookingId],
//                 [field]: field === 'notes' ? value : Number(value),
//                 bookingId
//             };

//             console.log('Updated booking data:', updatedBooking);

//             return {
//                 ...prev,
//                 [bookingId]: updatedBooking
//             };
//         });
//     };


//     const handleSplitClick = (booking: BookingID) => {
//         const currentId = booking.split_id || booking.booking_id;
//         setOpenSplitForms(prev => {
//             const newSet = new Set(prev);
//             if (newSet.has(currentId)) {
//                 newSet.delete(currentId);
//             } else {
//                 newSet.add(currentId);
//                 setOpenNotesForms(prevNotes => {
//                     const notesSet = new Set(prevNotes);
//                     notesSet.delete(currentId);
//                     return notesSet;
//                 });
//             }
//             return newSet;
//         });

//         if (!openSplitForms.has(currentId)) {
//             const isAlreadySplit = (booking.split_id && booking.split_id.includes('-'));
//             const originalId = isAlreadySplit ?
//                 (booking.split_id?.split('-')[0] || booking.booking_id) :
//                 booking.booking_id;

//             setSplitData(prev => ({
//                 ...prev,
//                 [currentId]: {
//                     ...prev[currentId],
//                     bookingId: currentId,
//                     totalQuantity: booking.total_quantity,
//                     large_box_quantity: booking.large_box_quantity || 0,
//                     medium_box_quantity: booking.medium_box_quantity || 0,
//                     small_box_quantity: booking.small_box_quantity || 0,
//                     notes: booking.notes,
//                     total_pieces: booking.total_pieces,
//                     cbm_value: booking.cbm_value,
//                     nrml_payment_quantity: booking.nrml_payment_quantity,
//                     invoice_value: booking.invoice_value,
//                     originalBookingId: originalId,
//                     isNewSplit: !isAlreadySplit
//                 }
//             }));
//         }
//     };

//     const modalStyles: React.CSSProperties = {
//         position: 'fixed',
//         top: 0,
//         left: 0,
//         right: 0,
//         bottom: 0,
//         backgroundColor: 'rgba(0, 0, 0, 0.5)',
//         display: showAddbookingpopup ? 'flex' : 'none',
//         alignItems: 'center',
//         justifyContent: 'center',
//         zIndex: 1050
//     };

//     const modalContentStyles: React.CSSProperties = {
//         width: '1200px',
//         maxWidth: '100%'
//     };

//     const bulkRemoveModalStyles: React.CSSProperties = {
//         position: 'fixed',
//         top: 0,
//         left: 0,
//         right: 0,
//         bottom: 0,
//         backgroundColor: 'rgba(0, 0, 0, 0.85)', // Darker overlay
//         display: isBulkRemoving ? 'flex' : 'none',
//         alignItems: 'center',
//         justifyContent: 'center',
//         zIndex: 9999, // Very high z-index to cover everything
//         cursor: 'not-allowed' // Show not-allowed cursor
//     };

//     const bulkRemoveContentStyles: React.CSSProperties = {
//         backgroundColor: 'white',
//         borderRadius: '8px',
//         padding: '30px',
//         maxWidth: '500px',
//         width: '90%',
//         textAlign: 'center',
//         boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)'
//     };


//     console.log("selectedBookingIds", selectedBookingIds)
//     console.log("splitData", splitData)
//     console.log("originalBookings", originalBookings)
//     console.log("after split succss after open", issplitopen)
//     console.log("Bokkingdetails", Bokkingdetails)
//     console.log("selectedBookingIds", selectedBookingIds)
//     console.log("splitBookingIds", splitBookingIds)
//     console.log("updatedBookingIds", updatedBookingIds)
//     console.log("bookingid", bookingId)
//     console.log("allSelectedBookingIds", allSelectedBookingIds)

//     const handleSelectChange = async (selected: BookingOption[] | null, actionMeta: any) => {
//         if (actionMeta.action === 'remove-value') {
//             const removedId = actionMeta.removedValue.value;

//             if (!splitBookingIds.has(removedId)) {
//                 handleBookingSelection(removedId);
//             }
//         } else if (actionMeta.action === 'select-option') {
//             const addedId = actionMeta.option.value;

//             if (addedId === SELECT_ALL_OPTION.value) {
//                 // Show confirmation dialog for "Select All"
//                 setShowSelectAllConfirm(true);
//                 return; // Don't proceed further until user confirms
//             }

//             handleBookingSelection(addedId);
//         } else if (actionMeta.action === 'clear') {
//             const idsToRemove = selectedBookingIds.filter(id => !splitBookingIds.has(id));

//             const shouldShowLoader = idsToRemove.length > 0;

//             if (shouldShowLoader) {
//                 // Set unloading blocked BEFORE showing loader
//                 setIsUnloadingBlocked(true);
//                 setIsBulkRemoving(true);
//                 setBulkRemoveProgress({ current: 0, total: idsToRemove.length });
//             }

//             let completedCount = 0;
//             const updatePromises = idsToRemove.map(async (id, index) => {
//                 try {
//                     await makeApiCall("https://backendcway.causewaycargo.com/superadmin/updatebookingstatus", "PUT", {
//                         booking_id: id,
//                         checked_status: "No"
//                     });

//                     completedCount++;
//                     if (shouldShowLoader) {
//                         setBulkRemoveProgress({ current: completedCount, total: idsToRemove.length });
//                     }
//                 } catch (error) {
//                     console.error(`Error updating booking ${id} status:`, error);
//                     completedCount++;
//                     if (shouldShowLoader) {
//                         setBulkRemoveProgress({ current: completedCount, total: idsToRemove.length });
//                     }
//                 }
//             });

//             // Wait for all API calls to complete
//             await Promise.all(updatePromises);

//             // Reset unloading block AFTER completion
//             setIsUnloadingBlocked(false);

//             // Hide loader
//             if (shouldShowLoader) {
//                 setIsBulkRemoving(false);
//                 setBulkRemoveProgress({ current: 0, total: 0 });
//             }

//             // Keep only split booking IDs in selection
//             const splitOptions = selectedOptions.filter(option => splitBookingIds.has(option.value));
//             setSelectedOptions(splitOptions);
//             setSelectedBookingIds(splitOptions.map(opt => opt.value));
//             setAllSelectedBookingIds(splitOptions.map(opt => opt.value));
//             return;
//         }

//         // Rest of the existing code...
//         let newSelectedOptions: BookingOption[] = [];

//         if (selected) {
//             newSelectedOptions = selected.filter(option => option.value !== SELECT_ALL_OPTION.value);

//             splitBookingIds.forEach(splitId => {
//                 if (!newSelectedOptions.some(opt => opt.value === splitId)) {
//                     const splitBooking = getBookingDetails(splitId);
//                     if (splitBooking) {
//                         newSelectedOptions.push({
//                             value: splitId,
//                             label: splitId,
//                             booking: splitBooking,
//                             isDisabled: true
//                         });
//                     }
//                 }
//             });
//         } else {
//             Array.from(splitBookingIds).forEach(splitId => {
//                 const splitBooking = getBookingDetails(splitId);
//                 if (splitBooking) {
//                     newSelectedOptions.push({
//                         value: splitId,
//                         label: splitId,
//                         booking: splitBooking,
//                         isDisabled: true
//                     });
//                 }
//             });
//         }

//         setSelectedOptions([...newSelectedOptions]);
//         setSelectedBookingIds(newSelectedOptions.map(opt => opt.value));
//         setAllSelectedBookingIds(newSelectedOptions.map(opt => opt.value));
//     };


//     const formatOptionLabel = ({ value, booking }: BookingOption) => {

//         if (value === "Select All") {
//             return (
//                 <div className="d-flex align-items-center justify-content-between w-100 px-2 py-1 fw-bold">
//                     {value}
//                 </div>
//             );
//         }

//         return (
//             <div className="d-flex align-items-center justify-content-between w-100 px-2 py-1">
//                 <span className="fw-bold">{value}</span>
//                 {booking?.booking_mode === "split booking" ? (
//                     <span className="text-muted small d-flex gap-2">
//                         <span>L: <strong>{booking.large_box_quantity || 0}</strong></span>
//                         <span>M: <strong>{booking.medium_box_quantity || 0}</strong></span>
//                         <span>S: <strong>{booking.small_box_quantity || 0}</strong></span>
//                     </span>
//                 ) : (
//                     <span className="text-muted small">
//                         Qty: <strong>{booking?.total_quantity || 0}</strong>
//                     </span>
//                 )}
//             </div>
//         );
//     };

//     const MultiValueContainer = (props: any) => {
//         const { data, innerProps } = props;
//         const id = data.value;
//         const isSplitBooking = splitBookingIds.has(id);

//         return (
//             <div
//                 className={`badge d-flex align-items-center gap-2 p-3 m-1 ${isSplitBooking ? 'bg-primary' : 'bg-dark'}`}
//                 title={isSplitBooking ? "Split booking ID (cannot be removed)" : ""}
//             >
//                 {id}
//                 {!isSplitBooking && (
//                     <i
//                         className="fas fa-times cursor-pointer ml-2"
//                         onClick={(e) => {
//                             e.stopPropagation();
//                             removeBooking(id);
//                         }}
//                     ></i>
//                 )}
//             </div>
//         );
//     };

//     //--------------------------Notes Add---------------------------

//     const handleNotesIconClick = async (bookingKey: string) => {
//         setOpenNotesForms(prev => {
//             const newSet = new Set(prev);
//             if (newSet.has(bookingKey)) {
//                 newSet.delete(bookingKey);
//             } else {
//                 newSet.add(bookingKey);
//                 fetchNotesData(bookingKey);
//                 setOpenSplitForms(prevSplit => {
//                     const splitSet = new Set(prevSplit);
//                     splitSet.delete(bookingKey);
//                     return splitSet;
//                 });
//             }
//             return newSet;
//         });
//     };

//     const fetchNotesData = async (bookingKey: string) => {
//         try {
//             // setLoadingNotes(prev => ({ ...prev, [bookingKey]: true }));

//             const response = await fetch('https://backendcway.causewaycargo.com/superadmin/bookingnotes', {
//                 method: 'POST',
//                 headers: {
//                     "Content-Type": "application/json",
//                     auth: "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6Nywicm9sZSI6InN1cGVyX2FkbWluIiwiaWF0IjoxNzI2ODM0MzIwfQ.edXyvu0znbVoi5KU3iL8FPww3qe8LMU0oXOCwdZi8do",
//                 },
//                 body: JSON.stringify({ common_id: bookingKey }),
//             });

//             if (!response.ok) {
//                 const errorResult = await response.json();
//                 const message = errorResult.message || "Failed to fetch notes.";
//                 throw new Error(message);
//             }

//             const result = await response.json();

//             setNotesData(prev => ({
//                 ...prev,
//                 [bookingKey]: result?.data?.[0]?.notes || "",
//             }));
//         } catch (err) {
//             console.error("Failed to fetch notes:", err);
//             toast.error(err instanceof Error ? err.message : "An unexpected error occurred while fetching notes.");
//         } finally {
//             setLoadingNotes(prev => ({ ...prev, [bookingKey]: false }));
//         }
//     };

//     const handleNotesSubmit = async (bookingKey: string) => {
//         const note = notesData[bookingKey]?.trim();

//         if (!note) {
//             toast.error("Please enter a note before submitting.");
//             return;
//         }

//         try {
//             setLoadingNotes(prev => ({ ...prev, [bookingKey]: true }));

//             const res = await fetch('https://backendcway.causewaycargo.com/superadmin/updatebookingnotes', {
//                 method: 'PUT',
//                 headers: {
//                     "Content-Type": "application/json",
//                     auth: "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6Nywicm9sZSI6InN1cGVyX2FkbWluIiwiaWF0IjoxNzI2ODM0MzIwfQ.edXyvu0znbVoi5KU3iL8FPww3qe8LMU0oXOCwdZi8do",
//                 },
//                 body: JSON.stringify({
//                     common_id: bookingKey,
//                     notes: note,
//                 }),
//             });

//             const result = await res.json();

//             if (result.status === 'success') {
//                 toast.success("Notes updated successfully!");
//                 // Optional: clear the notes field or close modal
//                 setNotesData(prev => ({ ...prev, [bookingKey]: '' }));
//                 setOpenNotesForms(prev => {
//                     const newSet = new Set(prev);
//                     newSet.delete(bookingKey);
//                     return newSet;
//                 });
//             } else {
//                 toast.error(result.message || "Failed to update notes.");
//             }
//         } catch (error) {
//             console.error("Error submitting notes:", error);
//             toast.error("An error occurred while submitting the notes.");
//         } finally {
//             setLoadingNotes(prev => ({ ...prev, [bookingKey]: true }));
//         }
//     };

//     useEffect(() => {
//         if (isBulkRemoving) {
//             // Prevent scrolling
//             document.body.style.overflow = 'hidden';
//             // Prevent any pointer events on underlying content
//             document.body.style.pointerEvents = 'none';
//         } else {
//             // Restore scrolling and pointer events
//             document.body.style.overflow = 'auto';
//             document.body.style.pointerEvents = 'auto';
//         }

//         return () => {
//             // Cleanup
//             document.body.style.overflow = 'auto';
//             document.body.style.pointerEvents = 'auto';
//         };
//     }, [isBulkRemoving]);


//     return (
//         <>
//             <Prompt
//                 when={shouldBlockNavigation}
//                 message={(nextLocation) => {
//                     const confirmLeave = window.confirm(
//                         'Are you sure you want to leave this page? Unsaved changes will be lost.'
//                     );
//                     if (confirmLeave) {
//                         sendCancelRequest();
//                     }
//                     return confirmLeave;
//                 }}
//             />
//             <div className="card">
//                 <div className="card-body">
//                     <form onSubmit={handleSubmit}>
//                         <h3 className="card-title">Add Shipment</h3>

//                         <div className="row mb-6">
//                             <div className="col-md-4">
//                                 <div className="form-group">
//                                     <label className="required form-label">Name</label>
//                                     <input
//                                         type="text"
//                                         className="form-control"
//                                         value={formData.name}
//                                         onChange={(e) => handleInputChange('name', e.target.value)}
//                                         required
//                                     />
//                                 </div>
//                             </div>

//                             <div className="col-md-4">
//                                 <div className="form-group">
//                                     <label className="required form-label">Originating Hub</label>
//                                     <select
//                                         className="form-select"
//                                         value={formData.originating_hub}
//                                         onChange={(e) => handleInputChange('originating_hub', e.target.value)}
//                                         required
//                                     >
//                                         <option value="">Select Hub Address</option>
//                                         {hubData &&
//                                             hubData.map((hub, index) => (
//                                                 <option key={index} value={hub.name}>{hub.name}</option>
//                                             ))}
//                                     </select>
//                                 </div>
//                             </div>

//                             <div className="col-md-4">
//                                 <div className="form-group">
//                                     <label className="required form-label">Destination Hub</label>
//                                     <select
//                                         className="form-select"
//                                         value={formData.destination_hub}
//                                         onChange={(e) => handleInputChange('destination_hub', e.target.value)}
//                                         required
//                                     >
//                                         <option value="">Select Hub Address</option>
//                                         {hubData &&
//                                             hubData.map((hub, index) => (
//                                                 <option key={index} value={hub.name}>{hub.name}</option>
//                                             ))}
//                                     </select>
//                                 </div>
//                             </div>
//                             <div>

//                             </div>
//                         </div>

//                         <div className="row mb-6">
//                             <div className="col-md-4">
//                                 <div className="form-group">
//                                     <label className="required form-label">Date of Journey</label>
//                                     <input
//                                         type="date"
//                                         className="form-control"
//                                         value={formData.date_of_journey}
//                                         min={new Date().toISOString().split("T")[0]} // Prevents past dates
//                                         onFocus={(e) => (e.target as any).showPicker?.()} // Bypass TypeScript error
//                                         onClick={(e) => (e.target as any).showPicker?.()} // Opens calendar on click
//                                         onChange={(e) => handleInputChange("date_of_journey", e.target.value)}
//                                         required
//                                         placeholder='enter date'
//                                     />


//                                 </div>
//                             </div>
//                         </div>

//                         <div className="row">
//                             <div className="col-12 d-flex justify-content-end">
//                                 <button
//                                     type="button"
//                                     className="btn btn-warning"
//                                     onClick={() => {
//                                         fetchAllbookingidforship();
//                                         setShowaddbookingPopup(true);
//                                     }}
//                                 >
//                                     Add Bookings
//                                 </button>
//                             </div>
//                         </div>
//                         {afterAddjourney && Bokkingdetails?.length > 0 && (
//                             <div className="mt-4 ">
//                                 <h4>Booking Details</h4>
//                                 <div className="table-responsive border-top mb-5 p-2" style={{ maxHeight: '400px', overflowY: 'auto' }}>
//                                     <table className="table table-row-bordered table-hover">
//                                         <thead>
//                                             <tr className="fw-bold fs-6 text-dark-800" >
//                                                 <th className='p-3'>S.No </th>
//                                                 <th className='p-3'>Booking ID</th>
//                                                 <th className='p-3'>Consignor</th>
//                                                 <th className='p-3'>Consignee</th>
//                                                 <th className='p-3'>Booking Mode</th>
//                                                 <th className='p-3'>Total Quantity</th>
//                                                 <th className='p-3'>Large Journey Quantity</th>
//                                                 <th className='p-3'>Medium Journey Quantity</th>
//                                                 <th className='p-3'>Small Journey Quantity</th>
//                                                 <th className='p-3'>Shipment Status</th>

//                                             </tr>
//                                         </thead>
//                                         <tbody   >
//                                             {Bokkingdetails.map((booking, index) => (
//                                                 <tr key={booking.id} className=''>
//                                                     <td className='p-3'>{index + 1}</td>
//                                                     <td className='p-3'>{booking.booking_id || booking.split_id}</td>
//                                                     <td className='p-3'>{booking.consignor}</td>
//                                                     <td className='p-3'>{booking.consignee}</td>
//                                                     <td className='p-3'>{booking.booking_mode}</td>
//                                                     <td className='p-3'>{booking.total_quantity}</td>

//                                                     {/* Show additional fields for split bookings */}
//                                                     {booking.booking_mode === "split booking" ? (
//                                                         <>
//                                                             <td className='p-3'>{booking.large_box_quantity || "-"}</td>
//                                                             <td className='p-3'>{booking.medium_box_quantity || "-"}</td>
//                                                             <td className='p-3'>{booking.small_box_quantity || "-"}</td>
//                                                         </>
//                                                     ) : (
//                                                         <>
//                                                             <td className='p-3'>0</td>
//                                                             <td className='p-3'>0</td>
//                                                             <td className='p-3'>0</td>
//                                                         </>
//                                                     )}
//                                                     <td>{booking.shipment_status === "collected to shipment" ? "Shipment progress" : booking.shipment_status}</td>

//                                                 </tr>
//                                             ))}
//                                         </tbody>
//                                     </table>
//                                 </div>
//                             </div>
//                         )}
//                         {afterAddjourney && Bokkingdetails?.length > 0 && (<div className="d-flex gap-2 justify-content-end">
//                             <button
//                                 type="button"
//                                 className="btn btn-warning"
//                                 onClick={() => {
//                                     const confirmCancel = window.confirm("Are you sure you want to cancel?");
//                                     if (confirmCancel) {
//                                         afterAddJournyCancel();
//                                         setAllSelectedBookingIds([]);
//                                         setSelectedBookingIds([]);
//                                         // updatedBookingIds = [];
//                                         setAfterAddjourney(false)
//                                     }
//                                 }}
//                             >
//                                 Cancel
//                             </button>
//                             <button
//                                 type="submit"
//                                 className="btn btn-primary"
//                                 onClick={() => { }}
//                                 disabled={loading} // This prevents clicking during loading
//                             >
//                                 {loading ? (
//                                     <>
//                                         Submitting...
//                                         <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
//                                     </>
//                                 ) : (
//                                     "Submit"
//                                 )}
//                             </button>
//                         </div>)}
//                     </form>
//                 </div>
//                 <ToastContainer />

//             </div>


//             {/* split popup */}

//             <div style={modalStyles}>
//                 <div className="modal-dialog" style={modalContentStyles}>
//                     <div className="modal-content">
//                         <div className="modal-header">
//                             <h5 className="modal-title">Select Bookings</h5>
//                             <button
//                                 type="button"
//                                 className={`btn-close cursor-pointer ${selectedBookingIds.length > 0 ? "hover:cursor-not-allowed opacity-50" : ""}`}
//                                 onClick={() =>
//                                     setShowaddbookingPopup(false)
//                                 }
//                                 disabled={selectedBookingIds.length > 0}
//                             ></button>

//                         </div>
//                         <div className="modal-body">
//                             <div className="form-group mb-4">
//                                 <div className="position-relative" ref={dropdownRef}>
//                                     <Select
//                                         isMulti
//                                         id="booking-select"
//                                         name="booking-select"
//                                         value={selectedOptions}
//                                         onChange={handleSelectChange}
//                                         options={convertBookingsToOptions()}
//                                         formatOptionLabel={formatOptionLabel}
//                                         components={{
//                                             MultiValueContainer,
//                                             MultiValueRemove: (props: any) => {
//                                                 const { data } = props;
//                                                 if (splitBookingIds.has(data.value)) {
//                                                     return null;
//                                                 }
//                                                 return (
//                                                     <components.MultiValueRemove {...props} />
//                                                 );
//                                             }
//                                         }}
//                                         closeMenuOnSelect={false}
//                                         placeholder="Choose Booking ID"
//                                         noOptionsMessage={() => "No bookings available"}
//                                         isClearable={true}
//                                         styles={{
//                                             control: (provided: any) => ({
//                                                 ...provided,
//                                                 cursor: 'pointer',
//                                                 minWidth: '200px',
//                                                 width: '100%',
//                                                 outline: 'none',
//                                                 maxHeight: '150px',
//                                                 overflow: 'auto',
//                                             }),
//                                             multiValue: (provided: any, state: any) => ({
//                                                 ...provided,
//                                                 margin: 0,
//                                                 padding: 0,
//                                                 backgroundColor: splitBookingIds.has(state.data.value) ? '#e6f7ff' : provided.backgroundColor,
//                                                 borderColor: splitBookingIds.has(state.data.value) ? '#1890ff' : provided.borderColor,
//                                             }),
//                                             multiValueRemove: (provided: any, state: any) => ({
//                                                 ...provided,
//                                                 display: splitBookingIds.has(state.data.value) ? 'none' : 'flex',
//                                             }),
//                                         }}
//                                         className="w-100"
//                                         menuPlacement="auto"
//                                     />

//                                     <div>

//                                     </div>
//                                 </div>
//                             </div>

//                             {!isDropdownOpen && selectedBookingIds.length > 0 && (
//                                 <div className="mt-4 border p-3 " style={{ maxHeight: '300px', overflowY: 'auto' }}>
//                                     <h6 className="mb-3 mt-3">Selected Bookings - {selectedBookingIds.length}</h6>
//                                     <div className="table-responsive">
//                                         <table className="table table-row-bordered table-hover align-middle">
//                                             <thead>
//                                                 <tr className="fw-bold fs-6 text-gray-800">
//                                                     <th className="text-start">Actions</th>
//                                                     <th className="text-center">Booking ID</th>
//                                                     <th className="text-center">Booking Mode</th>
//                                                     <th className="text-center">Consignor</th>
//                                                     <th className="text-center">Consignee</th>
//                                                     <th className="text-center">Total Quantity</th>
//                                                     <th className="text-center">Actions</th>
//                                                     {/* <th className="text-center">Notes</th> */}
//                                                 </tr>
//                                             </thead>
//                                             <tbody>
//                                                 {selectedBookingIds.map((bookingId) => {
//                                                     const booking = getBookingDetails(bookingId);
//                                                     if (!booking) return null;

//                                                     const isSpitOpen = splitData[bookingId];

//                                                     return (
//                                                         <>
//                                                             <tr key={bookingId}>
//                                                                 {/* Left-aligned Remove button */}
//                                                                 <td className="text-start">
//                                                                     <button
//                                                                         type="button"
//                                                                         className="btn btn-sm btn-danger"
//                                                                         onClick={() => removeBooking(booking.booking_id || booking.split_id)}
//                                                                     >
//                                                                         Remove
//                                                                     </button>
//                                                                 </td>

//                                                                 {/* Center-aligned text for table content */}
//                                                                 <td className="text-center">{booking.booking_id ? booking.booking_id : booking.split_id || '-'}</td>
//                                                                 <td className="text-capitalize text-center">{booking.booking_mode}</td>
//                                                                 <td className="text-center" title={booking.consignor} style={{ cursor: "pointer" }}>
//                                                                     {booking?.consignor?.length > 20 ? `${booking?.consignor?.slice(0, 20)}...` : booking.consignor}
//                                                                 </td>
//                                                                 <td className="text-center" title={booking.consignee} style={{ cursor: "pointer" }}>
//                                                                     {booking?.consignee?.length > 20 ? `${booking?.consignee?.slice(0, 20)}...` : booking.consignee}
//                                                                 </td>

//                                                                 <td className="text-center">{booking.total_quantity}</td>

//                                                                 {/* Right-aligned Split button */}
//                                                                 {/* <td className="text-start">
//                                                                     <button
//                                                                         type="button"
//                                                                         className="btn btn-sm btn-warning"
//                                                                         onClick={() => handleSplitClick(booking)}
//                                                                     >
//                                                                         Split
//                                                                     </button>
//                                                                 </td> */}

//                                                                 <td className='text-end'>
//                                                                     <button
//                                                                         className='btn btn-icon btn-light btn-hover-primary btn-sm me-1'
//                                                                         onClick={() => handleSplitClick(booking)}
//                                                                     >
//                                                                         <KTSVG path='/media/icons/duotune/arrows/arr030.svg' className='svg-icon-3' />
//                                                                     </button>

//                                                                     <button
//                                                                         className='btn btn-icon btn-light btn-hover-danger btn-sm'
//                                                                         onClick={() => handleNotesIconClick(booking.booking_id || booking.split_id)}
//                                                                     >
//                                                                         <KTSVG path='/media/icons/duotune/communication/com007.svg' className='svg-icon-3' />
//                                                                     </button>

//                                                                 </td>

//                                                             </tr>
//                                                             {openSplitForms.has(booking.booking_id || booking.split_id) && (
//                                                                 <tr>
//                                                                     <td colSpan={7}>
//                                                                         <div className="d-flex flex-column gap-3 p-3 bg-light">
//                                                                             {booking.booking_mode === 'normal booking' ? (
//                                                                                 // Normal Booking Fields
//                                                                                 <div className="d-flex gap-3 align-items-start flex-wrap">
//                                                                                     <div className="form-group mb-0">
//                                                                                         <label className="required form-label">Total Quantity</label>
//                                                                                         <input
//                                                                                             type="text"
//                                                                                             className="form-control"
//                                                                                             value={splitData[bookingId]?.totalQuantity || ''}
//                                                                                             onChange={(e) => handleSplitInputChange(booking.booking_id || booking.split_id, 'totalQuantity', e.target.value)}
//                                                                                             onKeyDown={(e) => {
//                                                                                                 const allowedKeys = [
//                                                                                                     'Backspace',
//                                                                                                     'Delete',
//                                                                                                     'ArrowLeft',
//                                                                                                     'ArrowRight',
//                                                                                                     'Tab',
//                                                                                                 ];

//                                                                                                 if (
//                                                                                                     !/^\d$/.test(e.key) && // not a number key
//                                                                                                     !allowedKeys.includes(e.key)
//                                                                                                 ) {
//                                                                                                     e.preventDefault();
//                                                                                                 }
//                                                                                             }}
//                                                                                             {...(splitBookingIds.has(booking.booking_id || booking.split_id)
//                                                                                                 ? { readOnly: true }
//                                                                                                 : { required: true })}
//                                                                                             required
//                                                                                         />
//                                                                                     </div>
//                                                                                     {booking.payment_mode && (booking.payment_mode === 'Piece Rate Method' || booking.payment_mode === 'CBM Rate Method') && (
//                                                                                         <div className="form-group mb-0">
//                                                                                             <label className="required form-label">
//                                                                                                 {getPaymentFieldLabel(booking.payment_mode)}
//                                                                                             </label>
//                                                                                             <input
//                                                                                                 type="number"
//                                                                                                 className="form-control"
//                                                                                                 value={
//                                                                                                     booking.payment_mode === 'Piece Rate Method'
//                                                                                                         ? splitData[bookingId]?.total_pieces ?? 0
//                                                                                                         : booking.payment_mode === 'CBM Rate Method'
//                                                                                                             ? splitData[bookingId]?.cbm_value ?? 0 : ""

//                                                                                                 }
//                                                                                                 onChange={(e) =>
//                                                                                                     handleSplitInputChange(
//                                                                                                         booking.booking_id || booking.split_id,
//                                                                                                         (booking.payment_mode === 'Piece Rate Method'
//                                                                                                             ? 'total_pieces'
//                                                                                                             : booking.payment_mode === 'CBM Rate Method'
//                                                                                                                 ? 'cbm_value'
//                                                                                                                 : 'total_pieces') as keyof SplitData,
//                                                                                                         e.target.value
//                                                                                                     )
//                                                                                                 }


//                                                                                                 {...(splitBookingIds.has(booking.booking_id || booking.split_id)
//                                                                                                     ? { readOnly: true }
//                                                                                                     : { required: true })}
//                                                                                             />
//                                                                                         </div>
//                                                                                     )}

//                                                                                 </div>
//                                                                             ) : (
//                                                                                 // Split Booking Fields
//                                                                                 <div className="d-flex gap-3 align-items-start flex-wrap">
//                                                                                     <div className="form-group mb-0">
//                                                                                         <label className="required form-label">Large Box Quantity</label>
//                                                                                         <input
//                                                                                             type="text"
//                                                                                             className="form-control"
//                                                                                             value={splitData[bookingId]?.large_box_quantity ?? 0}
//                                                                                             onChange={(e) => handleSplitInputChange(booking.booking_id || booking.split_id, 'large_box_quantity', e.target.value)}
//                                                                                             onKeyDown={(e) => {
//                                                                                                 const allowedKeys = [
//                                                                                                     'Backspace',
//                                                                                                     'Delete',
//                                                                                                     'ArrowLeft',
//                                                                                                     'ArrowRight',
//                                                                                                     'Tab',
//                                                                                                 ];

//                                                                                                 if (
//                                                                                                     !/^\d$/.test(e.key) && // not a number key
//                                                                                                     !allowedKeys.includes(e.key)
//                                                                                                 ) {
//                                                                                                     e.preventDefault();
//                                                                                                 }
//                                                                                             }}
//                                                                                             {...(splitBookingIds.has(booking.booking_id || booking.split_id)
//                                                                                                 ? { readOnly: true }
//                                                                                                 : { required: true })}
//                                                                                         />
//                                                                                     </div>
//                                                                                     <div className="form-group mb-0">
//                                                                                         <label className="required form-label">Medium Box Quantity</label>
//                                                                                         <input
//                                                                                             type="text"
//                                                                                             className="form-control"
//                                                                                             value={splitData[bookingId]?.medium_box_quantity ?? 0}
//                                                                                             onChange={(e) => handleSplitInputChange(booking.booking_id || booking.split_id, 'medium_box_quantity', e.target.value)}
//                                                                                             onKeyDown={(e) => {
//                                                                                                 const allowedKeys = [
//                                                                                                     'Backspace',
//                                                                                                     'Delete',
//                                                                                                     'ArrowLeft',
//                                                                                                     'ArrowRight',
//                                                                                                     'Tab',
//                                                                                                 ];

//                                                                                                 if (
//                                                                                                     !/^\d$/.test(e.key) && // not a number key
//                                                                                                     !allowedKeys.includes(e.key)
//                                                                                                 ) {
//                                                                                                     e.preventDefault();
//                                                                                                 }
//                                                                                             }}
//                                                                                             {...(splitBookingIds.has(booking.booking_id || booking.split_id)
//                                                                                                 ? { readOnly: true }
//                                                                                                 : { required: true })}

//                                                                                         />
//                                                                                     </div>
//                                                                                     <div className="form-group mb-0">
//                                                                                         <label className="required form-label">Small Box Quantity</label>
//                                                                                         <input
//                                                                                             type="text"
//                                                                                             className="form-control"
//                                                                                             value={splitData[bookingId]?.small_box_quantity ?? 0}
//                                                                                             onChange={(e) => handleSplitInputChange(booking.booking_id || booking.split_id, 'small_box_quantity', e.target.value)}
//                                                                                             onKeyDown={(e) => {
//                                                                                                 const allowedKeys = [
//                                                                                                     'Backspace',
//                                                                                                     'Delete',
//                                                                                                     'ArrowLeft',
//                                                                                                     'ArrowRight',
//                                                                                                     'Tab',
//                                                                                                 ];

//                                                                                                 if (
//                                                                                                     !/^\d$/.test(e.key) && // not a number key
//                                                                                                     !allowedKeys.includes(e.key)
//                                                                                                 ) {
//                                                                                                     e.preventDefault();
//                                                                                                 }
//                                                                                             }}
//                                                                                             {...(splitBookingIds.has(booking.booking_id || booking.split_id)
//                                                                                                 ? { readOnly: true }
//                                                                                                 : { required: true })}
//                                                                                         />
//                                                                                     </div>
//                                                                                     {booking.payment_mode && (booking.payment_mode === 'Piece Rate Method' || booking.payment_mode === 'CBM Rate Method') && (
//                                                                                         <div className="form-group mb-0">
//                                                                                             <label className="required form-label">{getPaymentFieldLabel(booking.payment_mode)}</label>
//                                                                                             <input
//                                                                                                 type="text"
//                                                                                                 className="form-control"
//                                                                                                 value={
//                                                                                                     booking.payment_mode === 'Piece Rate Method'
//                                                                                                         ? splitData[bookingId]?.total_pieces ?? ''
//                                                                                                         : booking.payment_mode === 'CBM Rate Method'
//                                                                                                             ? splitData[bookingId]?.cbm_value ?? ''
//                                                                                                             : ''
//                                                                                                 }
//                                                                                                 onChange={(e) => {
//                                                                                                     const inputValue = e.target.value;

//                                                                                                     // Allow only numbers and one optional decimal point
//                                                                                                     const isValid = /^\d*\.?\d*$/.test(inputValue);

//                                                                                                     if (isValid || inputValue === '') {
//                                                                                                         handleSplitInputChange(
//                                                                                                             booking.booking_id || booking.split_id,
//                                                                                                             (booking.payment_mode === 'Piece Rate Method'
//                                                                                                                 ? 'total_pieces'
//                                                                                                                 : booking.payment_mode === 'CBM Rate Method'
//                                                                                                                     ? 'cbm_value'
//                                                                                                                     : 'total_pieces') as keyof SplitData,
//                                                                                                             inputValue
//                                                                                                         );
//                                                                                                     }
//                                                                                                 }}
//                                                                                                 {...(splitBookingIds.has(booking.booking_id || booking.split_id)
//                                                                                                     ? { readOnly: true }
//                                                                                                     : { required: true })}
//                                                                                             />

//                                                                                         </div>
//                                                                                     )}
//                                                                                 </div>
//                                                                             )}
//                                                                             <div className="form-group mb-3">
//                                                                                 <label className="required form-label ">Notes</label>
//                                                                                 <textarea
//                                                                                     className="form-control"
//                                                                                     rows={2}
//                                                                                     value={splitData[bookingId]?.notes || ''}
//                                                                                     onChange={(e) => handleSplitInputChange(booking.booking_id || booking.split_id, 'notes', e.target.value)}
//                                                                                     placeholder="Enter notes for split booking..."
//                                                                                     {...(splitBookingIds.has(booking.booking_id || booking.split_id)
//                                                                                         ? { readOnly: true }
//                                                                                         : { required: true })}
//                                                                                 />
//                                                                             </div>

//                                                                             <div className="d-flex gap-2 justify-content-end ">
//                                                                                 {splitBookingIds.has(booking.booking_id || booking.split_id) ? (
//                                                                                     // Show cancel button only for successfully split bookings
//                                                                                     <button
//                                                                                         type="button"
//                                                                                         className="btn btn-danger"
//                                                                                         onClick={() => handleSplitCancel(booking)}
//                                                                                         disabled={cancelingBookingIds.has(booking.booking_id || booking.split_id)}
//                                                                                     >
//                                                                                         {cancelingBookingIds.has(booking.booking_id || booking.split_id) ? (
//                                                                                             <>
//                                                                                                 <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
//                                                                                                 Canceling...
//                                                                                             </>
//                                                                                         ) : (
//                                                                                             'Cancel Split'
//                                                                                         )}
//                                                                                     </button>
//                                                                                 ) : (
//                                                                                     // Show split button for bookings that haven't been split yet
//                                                                                     <button
//                                                                                         type="button"
//                                                                                         className="btn btn-warning"
//                                                                                         onClick={() => handleSplitClick(booking)}
//                                                                                     >
//                                                                                         Close
//                                                                                     </button>
//                                                                                 )}

//                                                                                 <button
//                                                                                     type="button"
//                                                                                     className="btn btn-primary"
//                                                                                     onClick={() => handleSplitSubmit(booking.booking_id || booking.split_id)}
//                                                                                     disabled={
//                                                                                         splitData[bookingId]?.totalQuantity > booking.total_quantity ||
//                                                                                         splitBookingIds.has(booking.booking_id || booking.split_id) ||
//                                                                                         loadingBookingIds.has(booking.booking_id || booking.split_id)
//                                                                                     }
//                                                                                 >
//                                                                                     {loadingBookingIds.has(booking.booking_id || booking.split_id) ? (
//                                                                                         <>
//                                                                                             <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
//                                                                                             Processing...
//                                                                                         </>
//                                                                                     ) : (
//                                                                                         'Submit'
//                                                                                     )}
//                                                                                 </button>
//                                                                             </div>
//                                                                         </div>
//                                                                     </td>
//                                                                 </tr>
//                                                             )}

//                                                             {openNotesForms.has(booking.booking_id || booking.split_id) && (
//                                                                 <tr>
//                                                                     <td colSpan={7}>
//                                                                         <div className="d-flex flex-column gap-3 p-3 bg-light">
//                                                                             <div className="form-group mb-3">
//                                                                                 <label className="required form-label">Notes</label>
//                                                                                 <textarea
//                                                                                     className="form-control"
//                                                                                     rows={3}
//                                                                                     value={notesData[booking.booking_id || booking.split_id] || ""}
//                                                                                     onChange={(e) =>
//                                                                                         setNotesData((prev) => ({
//                                                                                             ...prev,
//                                                                                             [booking.booking_id || booking.split_id]: e.target.value,
//                                                                                         }))
//                                                                                     }
//                                                                                 />
//                                                                             </div>
//                                                                             <div className="d-flex gap-2 justify-content-end">
//                                                                                 <button
//                                                                                     type="button"
//                                                                                     className="btn btn-secondary"
//                                                                                     onClick={() =>
//                                                                                         setOpenNotesForms((prev) => {
//                                                                                             const newSet = new Set(prev);
//                                                                                             newSet.delete(booking.booking_id || booking.split_id);
//                                                                                             return newSet;
//                                                                                         })
//                                                                                     }
//                                                                                 >
//                                                                                     Close
//                                                                                 </button>
//                                                                                 <button
//                                                                                     type="button"
//                                                                                     className="btn btn-primary"
//                                                                                     onClick={() => handleNotesSubmit(booking.booking_id || booking.split_id)}
//                                                                                     disabled={loadingNotes[booking.booking_id || booking.split_id]}
//                                                                                 >
//                                                                                     {loadingNotes[booking.booking_id || booking.split_id] ? 'Submitting...' : 'Submit Notes'}
//                                                                                 </button>

//                                                                             </div>
//                                                                         </div>
//                                                                     </td>
//                                                                 </tr>
//                                                             )}

//                                                         </>
//                                                     );
//                                                 })}
//                                             </tbody>
//                                         </table>
//                                     </div>
//                                 </div>
//                             )}
//                         </div>
//                         {!isDropdownOpen && selectedBookingIds.length > 0 && (<div className="modal-footer">
//                             <button
//                                 type="button"
//                                 className="btn btn-dark"
//                                 disabled={selectedBookingIds.length === 0}
//                                 onClick={() => {
//                                     setShowaddbookingPopup(false);
//                                     setAfterAddjourney(true)
//                                     afterAddJourneshowdetails()
//                                 }}
//                             >
//                                 Add Journey
//                             </button>

//                         </div>)}
//                     </div>
//                 </div>
//             </div>


//             {isBulkRemoving && (
//                 <div style={bulkRemoveModalStyles}>
//                     <div style={bulkRemoveContentStyles}>
//                         {/* Animated Percentage in Center */}
//                         <div style={{
//                             display: 'flex',
//                             justifyContent: 'center',
//                             marginBottom: '24px',
//                             height: '50px'
//                         }}>
//                             <div style={{
//                                 display: 'flex',
//                                 alignItems: 'center',
//                                 fontSize: '40px',
//                                 fontWeight: '700',
//                                 fontFamily: 'sans-serif',
//                                 color: '#262626',
//                                 overflow: 'hidden',
//                                 height: '50px'
//                             }}>
//                                 {String(Math.round((bulkRemoveProgress.current / bulkRemoveProgress.total) * 100))
//                                     .padStart(2, '0')
//                                     .split('')
//                                     .map((digit, index) => (
//                                         <div
//                                             key={index}
//                                             style={{
//                                                 display: 'flex',
//                                                 flexDirection: 'column',
//                                                 alignItems: 'center',
//                                                 justifyContent: 'center',
//                                                 height: '50px',
//                                                 width: '28px',
//                                                 animation: 'slideUp 1s ease-in-out',
//                                                 position: 'relative'
//                                             }}
//                                         >
//                                             {[...Array(10)].map((_, i) => (
//                                                 <div
//                                                     key={i}
//                                                     style={{
//                                                         position: 'absolute',
//                                                         top: `${(i - parseInt(digit)) * 100}%`,
//                                                         opacity: i === parseInt(digit) ? 1 : 0.1,
//                                                         transition: 'top 0.3s ease, opacity 0.3s ease',
//                                                     }}
//                                                 >
//                                                     {i}
//                                                 </div>
//                                             ))}
//                                         </div>
//                                     ))}
//                                 <span style={{ fontSize: '20px', marginLeft: '4px' }}>%</span>
//                             </div>
//                         </div>

//                         {/* Progress Info */}
//                         <div className="text-center mb-3">
//                             <p className="mb-0" style={{ color: '#666', fontSize: '14px' }}>
//                                 {bulkRemoveProgress.current} of {bulkRemoveProgress.total} completed
//                             </p>
//                         </div>

//                         {/* Linear Progress Bar */}
//                         <div className="mb-4">
//                             <div
//                                 className="progress"
//                                 style={{
//                                     height: '6px',
//                                     backgroundColor: '#f0f0f0',
//                                     borderRadius: '3px',
//                                     overflow: 'hidden'
//                                 }}
//                             >
//                                 <div
//                                     className="progress-bar"
//                                     role="progressbar"
//                                     style={{
//                                         width: `${Math.round((bulkRemoveProgress.current / bulkRemoveProgress.total) * 100)}%`,
//                                         backgroundColor: '#C42A21',
//                                         transition: 'width 0.3s ease-in-out',
//                                         borderRadius: '3px'
//                                     }}
//                                     aria-valuenow={Math.round((bulkRemoveProgress.current / bulkRemoveProgress.total) * 100)}
//                                     aria-valuemin={0}
//                                     aria-valuemax={100}
//                                 />
//                             </div>
//                         </div>

//                         {/* Helper Text */}
//                         <div className="text-center">
//                             <p className="mb-2" style={{ color: '#666', fontSize: '14px' }}>
//                                 Please wait while we process your request...
//                             </p>
//                             <p className="mb-0" style={{ color: '#C42A21', fontSize: '13px', fontWeight: '500' }}>
//                                 <strong>Do not close this window</strong>
//                             </p>
//                         </div>
//                     </div>
//                 </div>
//             )}


//             {/* Select All Confirmation Popup */}
//             {showSelectAllConfirm && (
//                 <div style={{
//                     position: 'fixed',
//                     top: 0,
//                     left: 0,
//                     right: 0,
//                     bottom: 0,
//                     backgroundColor: 'rgba(0, 0, 0, 0.5)',
//                     display: 'flex',
//                     alignItems: 'center',
//                     justifyContent: 'center',
//                     zIndex: 9999
//                 }}>
//                     <div style={{
//                         backgroundColor: 'white',
//                         borderRadius: '8px',
//                         padding: '30px',
//                         maxWidth: '400px',
//                         width: '90%',
//                         textAlign: 'center',
//                         boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)'
//                     }}>
//                         <h5 className="mb-3">Confirm Select All</h5>
//                         <p className="mb-4" style={{ color: '#666' }}>
//                             Are you sure you want to select all available bookings? This will select {convertBookingsToOptions().filter(opt => opt.value !== SELECT_ALL_OPTION.value && !opt.isDisabled).length} bookings.
//                         </p>
//                         <div className="d-flex gap-3 justify-content-center">
//                             <button
//                                 type="button"
//                                 className="btn btn-secondary"
//                                 onClick={() => setShowSelectAllConfirm(false)}
//                                 style={{ minWidth: '80px' }}
//                             >
//                                 No
//                             </button>
//                             <button
//                                 type="button"
//                                 className="btn btn-primary"
//                                 onClick={handleBulkSelectAll}
//                                 style={{ minWidth: '80px' }}
//                             >
//                                 Yes
//                             </button>
//                         </div>
//                     </div>
//                 </div>
//             )}

//             {/* Bulk Selection Progress Modal */}
//             {isBulkSelecting && (
//                 <div style={bulkRemoveModalStyles}>
//                     <div style={bulkRemoveContentStyles}>
//                         {/* Animated Percentage in Center */}
//                         <div style={{
//                             display: 'flex',
//                             justifyContent: 'center',
//                             marginBottom: '24px',
//                             height: '50px'
//                         }}>
//                             <div style={{
//                                 display: 'flex',
//                                 alignItems: 'center',
//                                 fontSize: '40px',
//                                 fontWeight: '700',
//                                 fontFamily: 'sans-serif',
//                                 color: '#262626',
//                                 overflow: 'hidden',
//                                 height: '50px'
//                             }}>
//                                 {String(Math.round((bulkSelectProgress.current / bulkSelectProgress.total) * 100))
//                                     .padStart(2, '0')
//                                     .split('')
//                                     .map((digit, index) => (
//                                         <div
//                                             key={index}
//                                             style={{
//                                                 display: 'flex',
//                                                 flexDirection: 'column',
//                                                 alignItems: 'center',
//                                                 justifyContent: 'center',
//                                                 height: '50px',
//                                                 width: '28px',
//                                                 animation: 'slideUp 1s ease-in-out',
//                                                 position: 'relative'
//                                             }}
//                                         >
//                                             {[...Array(10)].map((_, i) => (
//                                                 <div
//                                                     key={i}
//                                                     style={{
//                                                         position: 'absolute',
//                                                         top: `${(i - parseInt(digit)) * 100}%`,
//                                                         opacity: i === parseInt(digit) ? 1 : 0.1,
//                                                         transition: 'top 0.3s ease, opacity 0.3s ease',
//                                                     }}
//                                                 >
//                                                     {i}
//                                                 </div>
//                                             ))}
//                                         </div>
//                                     ))}
//                                 <span style={{ fontSize: '20px', marginLeft: '4px' }}>%</span>
//                             </div>
//                         </div>

//                         {/* Progress Info */}
//                         <div className="text-center mb-3">
//                             <p className="mb-0" style={{ color: '#666', fontSize: '14px' }}>
//                                 {bulkSelectProgress.current} of {bulkSelectProgress.total} bookings selected
//                             </p>
//                         </div>

//                         {/* Linear Progress Bar */}
//                         <div className="mb-4">
//                             <div
//                                 className="progress"
//                                 style={{
//                                     height: '6px',
//                                     backgroundColor: '#f0f0f0',
//                                     borderRadius: '3px',
//                                     overflow: 'hidden'
//                                 }}
//                             >
//                                 <div
//                                     className="progress-bar"
//                                     role="progressbar"
//                                     style={{
//                                         width: `${Math.round((bulkSelectProgress.current / bulkSelectProgress.total) * 100)}%`,
//                                         backgroundColor: '#28a745', // Green for selection (different from removal's red)
//                                         transition: 'width 0.3s ease-in-out',
//                                         borderRadius: '3px'
//                                     }}
//                                     aria-valuenow={Math.round((bulkSelectProgress.current / bulkSelectProgress.total) * 100)}
//                                     aria-valuemin={0}
//                                     aria-valuemax={100}
//                                 />
//                             </div>
//                         </div>

//                         {/* Helper Text */}
//                         <div className="text-center">
//                             <p className="mb-2" style={{ color: '#666', fontSize: '14px' }}>
//                                 Please wait while we select all bookings...
//                             </p>
//                             <p className="mb-0" style={{ color: '#28a745', fontSize: '13px', fontWeight: '500' }}>
//                                 <strong>Do not close this window</strong>
//                             </p>
//                         </div>
//                     </div>
//                 </div>
//             )}
//         </>
//     );
// };

// export default ShipmentForm;